import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, MenuItem, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import {
  FaClipboard, FaCog, FaDesktop, FaDownload, FaExpand, FaEye, FaEyeSlash, FaFileExcel, FaFilePdf, FaPlay, FaPlug, FaPowerOff, FaPrint, FaStop, FaTrash, FaUserCheck,
} from "react-icons/fa";
import RFB from "@novnc/novnc";
import api from "../Api";
import ConsoleDialog from "./ConsoleDialog";
import ConsoleDownloadsDialog from "./ConsoleDownloadsDialog";
import RemoteMobileKeyboard from "./RemoteMobileKeyboard";

// Derive the websocket origin from the configured API base (falls back to the
// page origin), so wss:// points at scan.slowloris.in where nginx serves
// /flipkart-ws/ (gated by auth_request -> the scan backend).
function wsOrigin() {
  const apiBase =
    process.env.REACT_APP_API_BASE_URL_PROD ||
    process.env.REACT_APP_API_BASE_URL ||
    "";
  let origin = window.location.origin;
  try {
    if (apiBase) origin = new URL(apiBase, window.location.origin).origin;
  } catch {
    /* keep page origin */
  }
  return origin.replace(/^http/, "ws"); // http->ws, https->wss
}

const STATUS = {
  idle: { label: "Not connected", color: "default" },
  connecting: { label: "Connecting…", color: "warning" },
  connected: { label: "Live", color: "success" },
  error: { label: "Error", color: "error" },
  closed: { label: "Disconnected", color: "default" },
};

// Order mirrors the offline panel so muscle memory carries over.
// Crop & dedup is deliberately absent: it is not a choice, it is what finishing
// a print means. Printing always merges the labels, de-duplicates, crops and
// opens the result, so offering it as a tick box only invites half-done runs.
const OPS = [
  { key: "accept", label: "Accept", icon: "✅", def: true },
  { key: "print", label: "Print", icon: "🖨", def: true },
  { key: "rtd", label: "RTD", icon: "🚚", def: true },
  { key: "scan_rtd", label: "Scan RTD", icon: "📡", def: false },
  { key: "scan_rtd_all", label: "All Scan RTD", icon: "⚠", def: false },
  { key: "upload", label: "Upload to scan", icon: "⬆", def: false },
  { key: "otc", label: "Pickup OTC", icon: "🔢", def: false },
  { key: "record", label: "Record logs", icon: "📼", def: false },
];

const FLOW_KEYS = ["accept", "print", "rtd"];
const SCHEDULE_OPTIONS = [
  { key: "accept", label: "ACCEPT" },
  { key: "print", label: "PRINT" },
  { key: "rtd", label: "RTD (TODAY)" },
  { key: "upload", label: "UPLOAD TO SCAN" },
  { key: "otc", label: "PICKUP OTC" },
  { key: "multi_dims", label: "MULTI DIMS" },
  { key: "reconcile", label: "RECONCILE" },
  { key: "dne", label: "DNE RETURNS" },
  { key: "claim_60_120", label: "61–120 CLAIM" },
];

const todayIstDate = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const byType = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${byType.year}-${byType.month}-${byType.day}`;
};

const MULTI_DIM_STAGE_LABELS = {
  queued: "Waiting",
  checking: "Revalidating row",
  saving: "Saving dimensions",
  saved: "Dimensions saved",
  accepting: "Accepting order",
  accepted: "Order accepted",
  ticket_queueing: "Queueing ticket",
  ticket_queued: "Ticket queued",
  ticket_submitted: "Ticket submitted",
  ticket_existing: "Ticket already exists",
  retrying: "Retrying",
  confirmation_required: "Confirmation required",
  identity_pending: "Identity pending",
  intervention: "Needs intervention",
  error: "Error",
};

const MULTI_DIM_DRAFT_KEY = "flipkart-console-multi-dims-v1";
const readMultiDimDraft = () => {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(MULTI_DIM_DRAFT_KEY) || "{}");
    return {
      rows: Array.isArray(value.rows) ? value.rows : [],
      result: value.result && typeof value.result === "object" ? value.result : null,
    };
  } catch {
    return { rows: [], result: null };
  }
};

const supportActionColor = (action) => {
  const value = String(action || "").toUpperCase();
  if (value === "SHIP") return "success";
  if (value === "HOLD") return "error";
  if (value === "WAIT") return "warning";
  return "default";
};

const DNE_FINAL_STATUSES = ["SUBMITTED", "READY_MANUAL", "SUPPORT_NOT_NEEDED", "UPLOADED_PENDING_STATUS", "AUTO_RESOLVED_30_DAYS", "AUTO_RESOLVED", "INVALID"];
const DNE_BLOCKED_SELECT_STATUSES = ["READY_MANUAL", "SUPPORT_NOT_NEEDED", "UPLOADED_PENDING_STATUS", "AUTO_RESOLVED_30_DAYS", "AUTO_RESOLVED", "INVALID"];
const DNE_AUTO_FINAL_STATUSES = ["SUBMITTED", "ALREADY_RAISED_WAIT", "SUPPORT_NOT_NEEDED", "UPLOADED_PENDING_STATUS", "AUTO_RESOLVED_30_DAYS", "AUTO_RESOLVED", "INVALID"];
const DNE_AUTO_BLOCKED_SELECT_STATUSES = ["ALREADY_RAISED_WAIT", "SUPPORT_NOT_NEEDED", "UPLOADED_PENDING_STATUS", "AUTO_RESOLVED_30_DAYS", "AUTO_RESOLVED", "INVALID"];
const dneTicketLabel = (row) => {
  return row?.ticket_number || "";
};
const dneApprovedAmount = (row) => {
  const text = String(row?.last_response || "");
  const match = text.match(/Approved Amount:\s*([0-9]+(?:\.[0-9]+)?)/i);
  return match ? match[1] : "";
};
const isDneNotApproved = (row) => {
  const status = String(row?.response_status || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return /^not approved$/i.test(status);
};
const dneStatusLabel = (row) => {
  const status = String(row?.support_ticket_status || "").trim();
  const flipkartStatus = String(row?.response_status || "").trim();
  const normalizedFlipkartStatus = flipkartStatus.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  const responseText = String(row?.last_response || "");
  if (status === "READY_MANUAL") return "Ready for manual submit";
  if (status === "ALREADY_RAISED_WAIT") return "Already raised, waiting for SPF status";
  if (status === "SUPPORT_NOT_NEEDED") return "Support not needed";
  if (status === "UPLOADED_PENDING_STATUS") return "Uploaded, status pending";
  if (status === "SUBMITTED_NO_TICKET") return "Submitted, no ticket";
  if (status === "AUTO_RESOLVED_30_DAYS") return "Support not needed (completed after 30 days)";
  if (status === "AUTO_RESOLVED") return "Auto-resolved";
  if (status === "INVALID") return "Invalid item ID (Flipkart validation)";
  if (normalizedFlipkartStatus) {
    if (/claim approved/i.test(normalizedFlipkartStatus)) {
      const amount = dneApprovedAmount(row);
      return amount ? `Claim Approved Rs. ${amount}` : "Claim Approved";
    }
    if (/created individual incidents for each order item id/i.test(responseText)) {
      return "Separate tickets created, outcome pending";
    }
    if (/currently being reviewed by our team/i.test(responseText)) return "Under review";
    return normalizedFlipkartStatus.replace(/\b\w/g, (letter) => letter.toUpperCase());
  }
  return status;
};
const claimStatusColor = (row) => {
  const label = dneStatusLabel(row).toLowerCase();
  if (label.includes("claim approved")) return "success";
  if (label.includes("error") || label.includes("invalid")) return "error";
  if (label.includes("pending") || label.includes("queued")) return "warning";
  if (label.includes("submitted")) return "info";
  return "default";
};
const formatClaimAmount = (value) => new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(Number(value) || 0);

// MySQL DATETIME values are already India-local wall-clock times. The database
// driver serializes them with a trailing Z, so parsing with Date would add IST
// a second time. Display the stored fields directly instead.
const formatDatabaseDateTime = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return String(value || "");
  return `${match[3]}/${match[2]}/${match[1]}, ${match[4]}:${match[5]}:${match[6] || "00"}`;
};

const supportCandidateKey = (row) => [
  row?.account_name || "",
  row?.order_id || "",
  row?.issue_type || "multi_quantity",
].join("||");

const artifactName = (artifact) => String(artifact?.name || artifact?.relpath || "");
const isExcelArtifact = (artifact) => /\.xlsx?$/i.test(artifactName(artifact));
const artifactMimeType = (artifact) => (
  isExcelArtifact(artifact)
    ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    : "application/pdf"
);
const artifactDownloadName = (artifact) => (
  artifact?.name || (isExcelArtifact(artifact) ? "flipkart_sku_list.xlsx" : "labels.pdf")
);

export default function FlipkartConsolePage() {
  const screenRef = useRef(null);
  const rfbRef = useRef(null);
  const remoteFocusedRef = useRef(false);
  const pasteSinkRef = useRef(null);
  const pollRef = useRef(null);
  const runLogBoxRef = useRef(null);
  const completionAudioRef = useRef(null);
  const [status, setStatus] = useState("idle");
  const [consoleMode, setConsoleMode] = useState("remote");
  const [message, setMessage] = useState("");
  // Every account has its own always-open Chrome on the same server display;
  // switching just raises that account's window, so the single VNC stream keeps
  // running (no reconnect, no session churn).
  const [accounts, setAccounts] = useState([]);
  const [active, setActive] = useState("");
  const [switching, setSwitching] = useState("");
  // selection for a run (independent of which window is on screen)
  const [picked, setPicked] = useState([]);
  const [ops, setOps] = useState(OPS.filter((o) => o.def).map((o) => o.key));
  const [flowScope, setFlowScope] = useState("today");
  const [running, setRunning] = useState(false);
  const [stopping, setStopping] = useState(false);
  // Server truth: is ANY batch live right now (this tab, another tab, or the
  // scheduler)? Local `running` only knows about runs this tab started, so after
  // a refresh the STOP button used to disappear while work was still going.
  const [serverBusy, setServerBusy] = useState(false);
  const [runLog, setRunLog] = useState("");
  const [taskCompleteAlert, setTaskCompleteAlert] = useState(false);
  const [activeRunOps, setActiveRunOps] = useState([]);
  const [logOpen, setLogOpen] = useState(false);
  const [runDialogPos, setRunDialogPos] = useState(null);
  const [runDialogSize, setRunDialogSize] = useState(null);
  const runDialogDragRef = useRef(null);
  const runDialogResizeRef = useRef(null);
  const [dlOpen, setDlOpen] = useState(false);
  // Return OTC: a run asks START or END first, then shows the code(s) big.
  const [otcPickOpen, setOtcPickOpen] = useState(false);
  const [returnOtc, setReturnOtc] = useState(null);   // {mode, results:[{account,codes,reason}]}
  // The labels PDF a finished print produced, if any. It lives on the server;
  // the server cannot reach the user's printer, so it has to be pulled down to
  // whichever machine they are sitting at.
  const [pdf, setPdf] = useState(null);
  const [printReady, setPrintReady] = useState(false);
  const [latestAlert, setLatestAlert] = useState(null);
  const [attentionAlert, setAttentionAlert] = useState(null);
  // Blob URL of the finished labels PDF, embedded inline the moment it's ready
  // (an in-page <embed> can't be popup-blocked the way an auto window.open is).
  const [autoPdfUrl, setAutoPdfUrl] = useState(null);
  const [pdfPreviewVisible, setPdfPreviewVisible] = useState(true);
  // Browsable history of every merged Flipkart label PDF in the archive.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDeleteFile, setHistoryDeleteFile] = useState(null);
  const [historyDeleting, setHistoryDeleting] = useState(false);
  const [reconOpen, setReconOpen] = useState(false);
  const [reconBusy, setReconBusy] = useState(false);
  const [recon, setRecon] = useState(null);
  const [reconWarning, setReconWarning] = useState(null);
  const [missingTrackOpen, setMissingTrackOpen] = useState(false);
  const [selectedMissingIds, setSelectedMissingIds] = useState(() => new Set());
  const [reprintBusy, setReprintBusy] = useState(false);
  const [reprintResult, setReprintResult] = useState(null);
  const [skuBusy, setSkuBusy] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportManualOpen, setSupportManualOpen] = useState(false);
  const [supportPreviewOpen, setSupportPreviewOpen] = useState(false);
  const [supportDate, setSupportDate] = useState(todayIstDate);
  const [supportOrderIds, setSupportOrderIds] = useState("");
  const [supportDescription, setSupportDescription] = useState("");
  const [supportCandidates, setSupportCandidates] = useState([]);
  const [supportSelectedIds, setSupportSelectedIds] = useState(() => new Set());
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportTrackerSelectedIds, setSupportTrackerSelectedIds] = useState(() => new Set());
  const [supportBusy, setSupportBusy] = useState(false);
  const [supportQueueing, setSupportQueueing] = useState(false);
  const [supportTracking, setSupportTracking] = useState(false);
  const [supportExpanded, setSupportExpanded] = useState("");
  const [supportFilters, setSupportFilters] = useState({
    q: "",
    account: "all",
    status: "all",
    responseStatus: "all",
    issueType: "all",
    from: "",
    to: "",
  });
  const [dneOpen, setDneOpen] = useState(false);
  const [dneRows, setDneRows] = useState([]);
  const [dneSummary, setDneSummary] = useState({ approved_count: 0, approved_amount: 0 });
  const [dneSelectedIds, setDneSelectedIds] = useState(() => new Set());
  const [dneBusy, setDneBusy] = useState(false);
  const [dneQueueing, setDneQueueing] = useState(false);
  const [dneExpanded, setDneExpanded] = useState("");
  const [dneFilters, setDneFilters] = useState({
    q: "",
    account: "all",
    status: "all",
    ticketStatus: "all",
    from: "",
    to: "",
  });
  const [claim60120Open, setClaim60120Open] = useState(false);
  const [claim60120Rows, setClaim60120Rows] = useState([]);
  const [claim60120Summary, setClaim60120Summary] = useState({ approved_count: 0, approved_amount: 0 });
  const [claim60120SelectedIds, setClaim60120SelectedIds] = useState(() => new Set());
  const [claim60120Busy, setClaim60120Busy] = useState(false);
  const [claim60120Queueing, setClaim60120Queueing] = useState(false);
  const [claim60120Expanded, setClaim60120Expanded] = useState("");
  const [claim60120Filters, setClaim60120Filters] = useState({ q: "", account: "all", ticketStatus: "all" });
  const [multiDimOpen, setMultiDimOpen] = useState(false);
  const [multiDimBusy, setMultiDimBusy] = useState(false);
  const [multiDimSaving, setMultiDimSaving] = useState(false);
  const [multiDimRows, setMultiDimRows] = useState(() => readMultiDimDraft().rows);
  const [multiDimResult, setMultiDimResult] = useState(() => readMultiDimDraft().result);
  const [reconDate, setReconDate] = useState("");   // dispatch date (yyyy-mm-dd)
  const [schedule, setSchedule] = useState({ enabled: false, status: "loading", times: [], ops: [] });
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleRuns, setScheduleRuns] = useState([]);
  const [expandedRun, setExpandedRun] = useState("");
  const [scheduleSettingsOpen, setScheduleSettingsOpen] = useState(false);
  const [editSchedules, setEditSchedules] = useState([]);
  const [hSearch, setHSearch] = useState("");
  const [hDate, setHDate] = useState("");
  const [hSort, setHSort] = useState("newest");
  const [hShowSub, setHShowSub] = useState(false);
  const [remoteClip, setRemoteClip] = useState("");
  const [remoteCopied, setRemoteCopied] = useState(false);
  const [remoteCopyBlocked, setRemoteCopyBlocked] = useState(false);

  const copyRemoteToLocal = useCallback(async (text) => {
    const value = String(text || remoteClip || "");
    if (!value) return false;
    try {
      await navigator.clipboard.writeText(value);
      setRemoteCopied(true);
      setRemoteCopyBlocked(false);
      setTimeout(() => setRemoteCopied(false), 1800);
      return true;
    } catch {
      setRemoteClip(value);
      setRemoteCopyBlocked(true);
      return false;
    }
  }, [remoteClip]);

  // Natural Mac -> remote paste. A real browser paste event is a user gesture,
  // so clipboard text is available without a permission prompt. Feed it to VNC,
  // then issue Linux Chrome's Ctrl+V inside the focused remote screen.
  useEffect(() => {
    const onPaste = (event) => {
      const r = rfbRef.current;
      if (!r || !remoteFocusedRef.current) return;
      const text = event.clipboardData?.getData("text/plain") || "";
      if (!text) return;
      event.preventDefault();
      r.clipboardPasteFrom(text);
      setTimeout(() => {
        try {
          r.sendKey(0xffe3, "ControlLeft", true);
          r.sendKey(0x76, "KeyV", true);
          r.sendKey(0x76, "KeyV", false);
          r.sendKey(0xffe3, "ControlLeft", false);
          r.focus();
          pasteSinkRef.current?.remove();
          pasteSinkRef.current = null;
        } catch {
          /* the paste box remains available as fallback */
        }
      }, 80);
    };
    window.addEventListener("paste", onPaste, true);
    return () => window.removeEventListener("paste", onPaste, true);
  }, []);

  useEffect(() => {
    const pasteIntoRemote = async (event) => {
      if (!remoteFocusedRef.current || !rfbRef.current) return;
      const isCtrlPaste = event.ctrlKey && !event.metaKey;
      const isCmdPaste = event.metaKey;
      if (!(isCtrlPaste || isCmdPaste) || String(event.key).toLowerCase() !== "v") return;
      if (isCmdPaste) {
        event.preventDefault();
        event.stopPropagation();
        const r = rfbRef.current;
        r.sendKey(0xffe3, "ControlLeft", true);
        r.sendKey(0x76, "KeyV", true);
        r.sendKey(0x76, "KeyV", false);
        r.sendKey(0xffe3, "ControlLeft", false);
        return;
      }
      if (isCtrlPaste) {
        event.preventDefault();
        event.stopPropagation();
      }
      try {
        const text = await navigator.clipboard.readText();
        if (!text || !rfbRef.current) return;
        const r = rfbRef.current;
        r.clipboardPasteFrom(text);
        setTimeout(() => {
          r.sendKey(0xffe3, "ControlLeft", true);
          r.sendKey(0x76, "KeyV", true);
          r.sendKey(0x76, "KeyV", false);
          r.sendKey(0xffe3, "ControlLeft", false);
        }, 120);
      } catch {
        setRemoteCopyBlocked(true);
        setMessage("Clipboard permission blocked. Allow clipboard access for scan.slowloris.in, then try Cmd+V again.");
      }
    };
    window.addEventListener("keydown", pasteIntoRemote, true);
    return () => window.removeEventListener("keydown", pasteIntoRemote, true);
  }, []);

  // Auto-open the merged labels PDF the moment the server's crop&dedup produces
  // it (the run poll delivers pdf.relpath). Server-driven — no Mac-side helper.
  const autoOpenedRef = useRef(null);
  const lastRunAttentionRef = useRef("");

  const extractRunAttention = useCallback((text) => {
    const lines = String(text || "").split(/\r?\n/).reverse();
    const line = lines.find((value) =>
      /LOGGED OUT\s*[—-]\s*LOG IN NOW|NOT logged in|login\s*\+\s*OTP/i.test(value || "")
    );
    if (!line) return null;
    const clean = line
      .replace(/^\s*\d{2}:\d{2}:\d{2}\s*/, "")
      .replace(/[!🔴📢]/g, "")
      .trim();
    return { id: clean, text: clean };
  }, []);

  const speakAlert = useCallback((words) => {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    for (let i = 0; i < 2; i += 1) {
      const speech = new SpeechSynthesisUtterance(words);
      speech.volume = 1;
      speech.rate = 0.82;
      speech.pitch = 1;
      window.speechSynthesis.speak(speech);
    }
  }, []);

  const unlockCompletionAudio = useCallback(() => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const ctx = completionAudioRef.current || new AudioContext();
      completionAudioRef.current = ctx;
      if (ctx.state === "suspended") ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.03);
    } catch {
      /* best effort: browsers vary on audio unlock rules */
    }
  }, []);

  const playTaskCompleted = useCallback(() => {
    unlockCompletionAudio();
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        const ctx = completionAudioRef.current || new AudioContext();
        completionAudioRef.current = ctx;
        if (ctx.state === "suspended") ctx.resume();
        [0, 0.2, 0.4].forEach((offset, index) => {
          const start = ctx.currentTime + offset;
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.setValueAtTime(index === 1 ? 880 : 660, start);
          gain.gain.setValueAtTime(0.0001, start);
          gain.gain.exponentialRampToValueAtTime(0.18, start + 0.03);
          gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.16);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(start);
          osc.stop(start + 0.18);
        });
      }
    } catch {
      /* speech below is the fallback */
    }
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      const speech = new SpeechSynthesisUtterance("Task completed");
      speech.volume = 1;
      speech.rate = 0.85;
      speech.pitch = 1;
      window.speechSynthesis.speak(speech);
    }
  }, [unlockCompletionAudio]);

  const playReconcileWarning = useCallback(() => {
    unlockCompletionAudio();
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        const ctx = completionAudioRef.current || new AudioContext();
        completionAudioRef.current = ctx;
        if (ctx.state === "suspended") ctx.resume();
        [980, 520, 980, 520].forEach((frequency, index) => {
          const start = ctx.currentTime + index * 0.18;
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "square";
          osc.frequency.setValueAtTime(frequency, start);
          gain.gain.setValueAtTime(0.0001, start);
          gain.gain.exponentialRampToValueAtTime(0.22, start + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.14);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(start);
          osc.stop(start + 0.15);
        });
      }
    } catch {
      /* speech below is the fallback */
    }
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      const speech = new SpeechSynthesisUtterance("Warning. Flipkart reconcile failed. Scan tracking IDs are missing from PDF.");
      speech.volume = 1;
      speech.rate = 0.9;
      speech.pitch = 0.85;
      window.speechSynthesis.speak(speech);
    }
  }, [unlockCompletionAudio]);

  const playAttentionWarning = useCallback((text) => {
    unlockCompletionAudio();
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        const ctx = completionAudioRef.current || new AudioContext();
        completionAudioRef.current = ctx;
        if (ctx.state === "suspended") ctx.resume();
        [1180, 760, 1180, 760, 1180].forEach((frequency, index) => {
          const start = ctx.currentTime + index * 0.16;
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "square";
          osc.frequency.setValueAtTime(frequency, start);
          gain.gain.setValueAtTime(0.0001, start);
          gain.gain.exponentialRampToValueAtTime(0.24, start + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.12);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(start);
          osc.stop(start + 0.14);
        });
      }
    } catch {
      /* speech below is the fallback */
    }
    speakAlert(`Attention. ${String(text || "Flipkart needs login").replaceAll("_", " ")}. Manual intervention is required.`);
  }, [speakAlert, unlockCompletionAudio]);

  const stopCurrentAlert = useCallback(() => {
    window.speechSynthesis?.cancel();
    setLatestAlert(null);
    setAttentionAlert(null);
    setPrintReady(false);
    setTaskCompleteAlert(false);
    setReconWarning(null);
    setMessage("");
  }, []);

  useEffect(() => {
    if (!taskCompleteAlert) return undefined;
    playTaskCompleted();
    const timer = setTimeout(() => setTaskCompleteAlert(false), 2500);
    return () => clearTimeout(timer);
  }, [playTaskCompleted, taskCompleteAlert]);

  useEffect(() => {
    if (!reconWarning) return undefined;
    playReconcileWarning();
    const timer = setInterval(playReconcileWarning, 5000);
    return () => clearInterval(timer);
  }, [playReconcileWarning, reconWarning]);

  useEffect(() => {
    if (!logOpen) return;
    const el = runLogBoxRef.current;
    if (!el) return;
    const scrollToBottom = () => {
      el.scrollTop = el.scrollHeight;
    };
    requestAnimationFrame(scrollToBottom);
    const timer = setTimeout(scrollToBottom, 80);
    return () => clearTimeout(timer);
  }, [logOpen, runLog, returnOtc, pdf, autoPdfUrl]);

  useEffect(() => {
    if (!attentionAlert) return undefined;
    const announce = () => {
      playAttentionWarning(attentionAlert.text);
    };
    announce();
    const timer = setInterval(announce, 6000);
    return () => clearInterval(timer);
  }, [attentionAlert, playAttentionWarning]);

  // Fetch any archived file by relpath. PDFs can open inline; Excel workbooks
  // are downloaded so the OS/browser can hand them to Excel.
  const openArchiveFile = useCallback(async (relpath, name, asDownload) => {
    const isXlsx = /\.xlsx?$/i.test(String(name || relpath || ""));
    // Reserve the tab while this is still a direct click. Opening it after the
    // authenticated request finishes is treated as an unsolicited popup by Chrome.
    const popup = (!asDownload && !isXlsx) ? window.open("about:blank", "_blank") : null;
    if (popup) popup.opener = null;
    try {
      const { data } = await api.get("/api/archive/file", {
        params: { p: relpath, ...((asDownload || isXlsx) ? { dl: 1 } : {}) },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([data], {
        type: isXlsx
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf",
      }));
      if (asDownload || isXlsx) {
        const a = document.createElement("a");
        a.href = url; a.download = name || (isXlsx ? "flipkart_sku_list.xlsx" : "labels.pdf");
        document.body.appendChild(a); a.click(); a.remove();
      } else {
        if (popup && !popup.closed) popup.location.href = url;
        else setMessage("Popup blocked — use Download instead.");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (popup && !popup.closed) popup.close();
      setMessage(err?.response?.data?.error || err?.message || "Could not fetch the archive file.");
    }
  }, []);

  const openHistory = useCallback(async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/api/flipkart-console/labels-history", {
        params: hShowSub ? { sub: 1 } : undefined,
      });
      setHistory((data && data.files) || []);
    } catch {
      setHistory([]);
    }
    setHistoryLoading(false);
  }, [hShowSub]);

  const deleteHistoryFile = useCallback(async () => {
    if (!historyDeleteFile || historyDeleting) return;
    const file = historyDeleteFile;
    setHistoryDeleting(true);
    try {
      await api.delete("/api/archive/file", { data: { p: file.relpath } });
      setHistory((current) => current.filter((row) => row.relpath !== file.relpath));
      setPdf((current) => current?.relpath === file.relpath ? null : current);
      window.dispatchEvent(new CustomEvent("archive-file-deleted", {
        detail: { relpath: file.relpath },
      }));
      setMessage(`Deleted ${file.name}.`);
      setHistoryDeleteFile(null);
    } catch (err) {
      setMessage(err?.response?.data?.message || err?.message || "Could not delete the file.");
    } finally {
      setHistoryDeleting(false);
    }
  }, [historyDeleteFile, historyDeleting]);

  useEffect(() => {
    if (historyOpen) openHistory();
  }, [historyOpen, hShowSub, openHistory]);

  useEffect(() => {
    const onArchiveFileDeleted = (event) => {
      const relpath = event?.detail?.relpath;
      if (!relpath) return;
      setHistory((current) => current.filter((row) => row.relpath !== relpath));
      setPdf((current) => current?.relpath === relpath ? null : current);
    };
    window.addEventListener("archive-file-deleted", onArchiveFileDeleted);
    return () => window.removeEventListener("archive-file-deleted", onArchiveFileDeleted);
  }, []);

  const runReconcile = useCallback(async (date) => {
    setRecon(null);
    setReconWarning(null);
    setReprintResult(null);
    setReconBusy(true);
    setReconOpen(true);
    try {
      const { data } = await api.get("/api/flipkart-console/reconcile",
        date ? { params: { date } } : undefined);
      setRecon(data);
      if (data && data.dispatch_iso) setReconDate(data.dispatch_iso);
      const labels = Number(data?.total_pages || 0);
      const scan = Number(data?.tracking_match?.scan_total || 0);
      const missingTracking = Number(data?.tracking_match?.scan_only_count || 0);
      if (data?.ok && ((scan > 0 && labels < scan) || missingTracking > 0)) {
        const warning = { labels, scan, missing: Math.max(0, scan - labels), missingTracking };
        setReconWarning(warning);
        setMessage(
          missingTracking > 0
            ? `WARNING — ${missingTracking} scan tracking ID(s) are missing from PDF labels.`
            : `WARNING — Flipkart labels ${labels} are less than scan quantity ${scan}. Missing ${warning.missing}.`
        );
      }
    } catch (err) {
      setRecon({ ok: false, error: err?.response?.data?.error || err?.message || "failed" });
    }
    setReconBusy(false);
  }, []);

  const missingRows = useMemo(
    () => (recon?.tracking_match?.scan_only || []).filter((row) => row && row.id),
    [recon]
  );

  useEffect(() => {
    setSelectedMissingIds(new Set(missingRows.map((row) => String(row.id || "").trim()).filter(Boolean)));
    setReprintResult(null);
  }, [missingRows]);

  const selectedMissingRows = useMemo(
    () => missingRows.filter((row) => selectedMissingIds.has(String(row.id || "").trim())),
    [missingRows, selectedMissingIds]
  );

  const setAllMissingSelected = useCallback((checked) => {
    setSelectedMissingIds(checked
      ? new Set(missingRows.map((row) => String(row.id || "").trim()).filter(Boolean))
      : new Set());
  }, [missingRows]);

  const toggleMissingId = useCallback((id, checked) => {
    const key = String(id || "").trim();
    setSelectedMissingIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const runSelectedReprint = useCallback(async () => {
    if (!selectedMissingRows.length || reprintBusy) return;
    setReprintBusy(true);
    setReprintResult(null);
    setMessage(`Reprinting ${selectedMissingRows.length} selected Flipkart label(s)…`);
    try {
      const { data } = await api.post("/api/flipkart-console/reprint-selected", {
        items: selectedMissingRows.map((row) => ({
          id: row.id,
          account: row.account || "",
          order_id: row.order_id || "",
          order_item_id: row.order_item_id || "",
        })),
      });
      setReprintResult(data);
      if (data?.ok && data.relpath) {
        const artifact = { relpath: data.relpath, name: data.name, pages: data.pages };
        setPdf(artifact);
        setPrintReady(true);
        setTaskCompleteAlert(true);
        playTaskCompleted();
        setMessage(`Reprint ready — ${data.printed || data.pages || selectedMissingRows.length} selected label(s) merged.`);
      } else {
        setMessage(data?.error || "Reprint did not return a merged PDF.");
      }
    } catch (err) {
      const data = err?.response?.data;
      setReprintResult(data || null);
      setMessage(data?.error || err?.message || "Selected reprint failed.");
    }
    setReprintBusy(false);
  }, [playTaskCompleted, reprintBusy, selectedMissingRows]);

  const historyDates = useMemo(
    () => [...new Set(history.map((f) => f.date))],
    [history]
  );
  const visibleHistory = useMemo(() => {
    const q = hSearch.trim().toLowerCase();
    let list = history.filter(
      (f) => (!q || f.name.toLowerCase().includes(q)) && (!hDate || f.date === hDate)
    );
    list = [...list];
    if (hSort === "newest") list.sort((a, b) => b.mtime - a.mtime);
    else if (hSort === "oldest") list.sort((a, b) => a.mtime - b.mtime);
    else if (hSort === "largest") list.sort((a, b) => b.size - a.size);
    else if (hSort === "smallest") list.sort((a, b) => a.size - b.size);
    else if (hSort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [history, hSearch, hDate, hSort]);

  const downloadFlipkartSkuList = useCallback(async () => {
    if (skuBusy) return;
    if (!picked.length) {
      setMessage("Select account(s) first for Flipkart SKU List.");
      return;
    }
    setSkuBusy(true);
    unlockCompletionAudio();
    setMessage("Queued Flipkart SKU List — downloading fresh To Accept, To Pack and Upcoming CSVs…");
    setTaskCompleteAlert(false);
    setAttentionAlert(null);
    setLatestAlert(null);
    lastRunAttentionRef.current = "";
    setReturnOtc(null);
    setRunLog("Queued — SKU List downloads selected account CSVs only; no order processing.\n");
    setActiveRunOps(["sku_list"]);
    setLogOpen(true);
    setRunning(true);
    setPdf(null);
    setAutoPdfUrl((old) => { if (old) URL.revokeObjectURL(old); return null; });
    try {
      const { data } = await api.post("/api/flipkart-console/run", {
        accounts: picked,
        ops: ["sku_list"],
        mode: "start",
      });
      const id = data.id;
      let downloaded = false;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/api/flipkart-console/run/${id}`);
          if (st.log) {
            setRunLog(st.log);
            const runAttention = extractRunAttention(st.log);
            if (runAttention && runAttention.id !== lastRunAttentionRef.current) {
              lastRunAttentionRef.current = runAttention.id;
              setLatestAlert(runAttention);
              setAttentionAlert(runAttention);
              setMessage(`ATTENTION — ${runAttention.text}`);
              setLogOpen(true);
            }
          }
          if (st.skuList?.relpath && !downloaded) {
            downloaded = true;
            setPdf({ relpath: st.skuList.relpath, name: st.skuList.name });
            await openArchiveFile(st.skuList.relpath, st.skuList.name, true);
            const wr = st.skuList.woostro_restore_total || 0;
            const slw = st.skuList.slowloris_total || 0;
            setMessage(`Flipkart SKU List downloaded — WR ${wr}, SLOWLORIS ${slw}. Saved in Files.`);
          }
          if (st.done) {
            if (pollRef.current) {
              clearInterval(pollRef.current);
              pollRef.current = null;
            }
            setRunning(false);
            setSkuBusy(false);
            playTaskCompleted();
            setTaskCompleteAlert(true);
            setMessage(downloaded
              ? "Flipkart SKU List complete — workbook downloaded and archived."
              : "Flipkart SKU List complete — no workbook returned; check log.");
            setLogOpen(true);
          }
        } catch {
          /* keep polling; a blip shouldn't kill the view of a live run */
        }
      }, 2000);
    } catch (err) {
      setMessage(err?.response?.data?.error || err?.message || "Could not build Flipkart SKU List.");
      setRunning(false);
      setRunLog("");
      setLogOpen(false);
      setSkuBusy(false);
    }
  }, [extractRunAttention, openArchiveFile, picked, playTaskCompleted, skuBusy, unlockCompletionAudio]);

  const loadSupportTickets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(supportFilters).forEach(([key, value]) => {
        if (value && value !== "all") params.set(key, value);
      });
      const qs = params.toString();
      const { data } = await api.get(`/api/flipkart-console/support-tickets${qs ? `?${qs}` : ""}`);
      const rows = data.rows || [];
      setSupportTickets(rows);
      setSupportTrackerSelectedIds((prev) => {
        const trackable = new Set(rows
          .filter((row) => row.status === "SUBMITTED" && row.ticket_number)
          .map((row) => row.id));
        return new Set([...prev].filter((id) => trackable.has(id)));
      });
    } catch (err) {
      setMessage(err?.response?.data?.error || err?.message || "Could not load support tickets.");
    }
  }, [supportFilters]);

  const openSupportTickets = useCallback(async () => {
    setSupportOpen(true);
    setSupportDate(todayIstDate());
    setSupportManualOpen(false);
    setSupportPreviewOpen(false);
    setSupportCandidates([]);
    setSupportSelectedIds(new Set());
    setSupportTrackerSelectedIds(new Set());
    await loadSupportTickets();
  }, [loadSupportTickets]);

  useEffect(() => {
    if (supportOpen) loadSupportTickets();
  }, [loadSupportTickets, supportOpen]);

  const previewSupportTickets = useCallback(async () => {
    if (!picked.length) {
      setMessage("Select account(s) first for Support Tickets.");
      return;
    }
    setSupportBusy(true);
    setSupportPreviewOpen(true);
    try {
      const { data } = await api.post("/api/flipkart-console/support-tickets/preview", {
        accounts: picked,
        date: supportDate,
        orderIds: supportOrderIds,
        description: supportDescription,
      });
      const items = data.items || [];
      setSupportCandidates(items);
      setSupportSelectedIds(new Set(
        items
          .filter((row) => row?.existing?.status !== "SUBMITTED")
          .map((row) => supportCandidateKey(row))
      ));
      setMessage(`Support ticket preview ready — ${items.length} order(s).`);
      await loadSupportTickets();
    } catch (err) {
      setMessage(err?.response?.data?.error || err?.message || "Could not preview support tickets.");
    } finally {
      setSupportBusy(false);
    }
  }, [loadSupportTickets, picked, supportDate, supportDescription, supportOrderIds]);

  const queueSupportTickets = useCallback(async () => {
    const rows = supportCandidates.filter((row) => (
      row?.existing?.status !== "SUBMITTED" && supportSelectedIds.has(supportCandidateKey(row))
    ));
    if (!rows.length) {
      setMessage("No new/failed support-ticket rows to queue.");
      return;
    }
    const ok = window.confirm(
      `Raise ${rows.length} Flipkart support ticket(s), one separate ticket per order?\n\n` +
      `This will use the live seller assistance page in the selected account browser(s).`
    );
    if (!ok) return;
    setSupportQueueing(true);
    unlockCompletionAudio();
    setMessage("Queued support tickets — raising one ticket per order…");
    setTaskCompleteAlert(false);
    setAttentionAlert(null);
    setLatestAlert(null);
    lastRunAttentionRef.current = "";
    setRunLog("Queued — support ticket automation will record network requests and save ticket numbers.\n");
    setActiveRunOps(["support_ticket"]);
    setLogOpen(true);
    setRunning(true);
    try {
      const { data } = await api.post("/api/flipkart-console/support-tickets/queue", { items: rows });
      if (data.skipped) {
        setMessage(data.message || "All selected orders already have submitted tickets.");
        setRunning(false);
        setSupportQueueing(false);
        await loadSupportTickets();
        return;
      }
      const id = data.id;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/api/flipkart-console/run/${id}`);
          if (st.log) {
            setRunLog(st.log);
            const runAttention = extractRunAttention(st.log);
            if (runAttention && runAttention.id !== lastRunAttentionRef.current) {
              lastRunAttentionRef.current = runAttention.id;
              setLatestAlert(runAttention);
              setAttentionAlert(runAttention);
              setMessage(`ATTENTION — ${runAttention.text}`);
              setLogOpen(true);
            }
          }
          if (st.done) {
            if (pollRef.current) {
              clearInterval(pollRef.current);
              pollRef.current = null;
            }
            setRunning(false);
            setSupportQueueing(false);
            await loadSupportTickets();
            playTaskCompleted();
            setTaskCompleteAlert(true);
            setMessage("Support ticket run complete — ticket numbers saved where Flipkart returned them.");
            setLogOpen(true);
          }
        } catch {
          /* keep polling; a blip shouldn't kill the view of a live run */
        }
      }, 2000);
    } catch (err) {
      setRunning(false);
      setSupportQueueing(false);
      setMessage(err?.response?.data?.error || err?.message || "Could not queue support tickets.");
    }
  }, [
    extractRunAttention, loadSupportTickets, playTaskCompleted,
    supportCandidates, supportSelectedIds, unlockCompletionAudio,
  ]);

  const supportTrackerTrackableRows = useMemo(
    () => supportTickets.filter((row) => row.status === "SUBMITTED" && row.ticket_number),
    [supportTickets]
  );

  const supportTrackerTrackableIds = useMemo(
    () => supportTrackerTrackableRows.map((row) => row.id),
    [supportTrackerTrackableRows]
  );

  const supportTrackerSelectedRows = useMemo(
    () => supportTrackerTrackableRows.filter((row) => supportTrackerSelectedIds.has(row.id)),
    [supportTrackerSelectedIds, supportTrackerTrackableRows]
  );

  const supportTrackerAllVisibleSelected = useMemo(
    () => supportTrackerTrackableIds.length > 0 && supportTrackerTrackableIds.every((id) => supportTrackerSelectedIds.has(id)),
    [supportTrackerSelectedIds, supportTrackerTrackableIds]
  );

  const supportTrackerSomeVisibleSelected = useMemo(
    () => supportTrackerTrackableIds.some((id) => supportTrackerSelectedIds.has(id)),
    [supportTrackerSelectedIds, supportTrackerTrackableIds]
  );

  const trackSupportTicketReverts = useCallback(async () => {
    const submitted = supportTrackerSelectedRows;
    if (!supportTrackerSelectedIds.size) {
      setMessage("Tick submitted support ticket rows first.");
      return;
    }
    if (!submitted.length) {
      setMessage("Selected support ticket rows have no submitted Flipkart ticket number to track.");
      return;
    }
    const ok = window.confirm(
      `Track Flipkart response/status for ${submitted.length} selected submitted ticket(s)?\n\n` +
      `This opens the seller support ticket area in each account browser and saves the visible status/reply.`
    );
    if (!ok) return;
    setSupportTracking(true);
    unlockCompletionAudio();
    setMessage("Queued ticket revert tracking…");
    setTaskCompleteAlert(false);
    setAttentionAlert(null);
    setLatestAlert(null);
    lastRunAttentionRef.current = "";
    setRunLog("Queued — tracking Flipkart support ticket status/replies.\n");
    setActiveRunOps(["support_ticket_track"]);
    setLogOpen(true);
    setRunning(true);
    try {
      const { data } = await api.post("/api/flipkart-console/support-tickets/track", {
        ids: submitted.map((row) => row.id),
      });
      if (data.skipped) {
        setMessage(data.message || "No submitted tickets to track.");
        setRunning(false);
        setSupportTracking(false);
        return;
      }
      const id = data.id;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/api/flipkart-console/run/${id}`);
          if (st.log) {
            setRunLog(st.log);
            const runAttention = extractRunAttention(st.log);
            if (runAttention && runAttention.id !== lastRunAttentionRef.current) {
              lastRunAttentionRef.current = runAttention.id;
              setLatestAlert(runAttention);
              setAttentionAlert(runAttention);
              setMessage(`ATTENTION — ${runAttention.text}`);
              setLogOpen(true);
            }
          }
          if (st.done) {
            if (pollRef.current) {
              clearInterval(pollRef.current);
              pollRef.current = null;
            }
            setRunning(false);
            setSupportTracking(false);
            await loadSupportTickets();
            playTaskCompleted();
            setTaskCompleteAlert(true);
            setMessage("Ticket revert tracking complete — tracker refreshed.");
            setLogOpen(true);
          }
        } catch {
          /* keep polling */
        }
      }, 2000);
    } catch (err) {
      setRunning(false);
      setSupportTracking(false);
      setMessage(err?.response?.data?.error || err?.message || "Could not queue ticket tracking.");
    }
  }, [
    extractRunAttention, loadSupportTickets, playTaskCompleted,
    supportTrackerSelectedIds.size, supportTrackerSelectedRows, unlockCompletionAudio,
  ]);

  const loadDneReturns = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(dneFilters).forEach(([key, value]) => {
        if (value && value !== "all") params.set(key, value);
      });
      const qs = params.toString();
      const { data } = await api.get(`/api/flipkart-console/dne-returns${qs ? `?${qs}` : ""}`);
      setDneRows(data.rows || []);
      setDneSummary(data.summary || { approved_count: 0, approved_amount: 0 });
    } catch (err) {
      setMessage(err?.response?.data?.error || err?.message || "Could not load DNE returns.");
    }
  }, [dneFilters]);

  const openDneReturns = useCallback(async () => {
    setDneOpen(true);
    setDneSelectedIds(new Set());
    await loadDneReturns();
  }, [loadDneReturns]);

  useEffect(() => {
    if (dneOpen) loadDneReturns();
  }, [dneOpen, loadDneReturns]);

  const load60120Claims = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(claim60120Filters).forEach(([key, value]) => {
        if (value && value !== "all") params.set(key, value);
      });
      const qs = params.toString();
      const { data } = await api.get(`/api/flipkart-console/claims-60-120${qs ? `?${qs}` : ""}`);
      setClaim60120Rows(data.rows || []);
      setClaim60120Summary(data.summary || { approved_count: 0, approved_amount: 0 });
    } catch (err) {
      setMessage(err?.response?.data?.error || err?.message || "Could not load 60-120 claims.");
    }
  }, [claim60120Filters]);

  const open60120Claims = useCallback(async () => {
    setClaim60120Open(true);
    setClaim60120SelectedIds(new Set());
    await load60120Claims();
  }, [load60120Claims]);

  useEffect(() => {
    if (claim60120Open) load60120Claims();
  }, [claim60120Open, load60120Claims]);

  const dneRaiseableRows = useMemo(
    () => dneRows.filter((row) => Number(row.age_days) >= 0 && Number(row.age_days) <= 30 && !DNE_AUTO_FINAL_STATUSES.includes(row.support_ticket_status)),
    [dneRows]
  );

  const dneSelectableRows = useMemo(
    () => dneRows.filter((row) => Number(row.age_days) >= 0 && Number(row.age_days) <= 30 && !DNE_AUTO_BLOCKED_SELECT_STATUSES.includes(row.support_ticket_status)),
    [dneRows]
  );

  const supportSelectableCandidates = useMemo(
    () => supportCandidates.filter((row) => row?.existing?.status !== "SUBMITTED"),
    [supportCandidates]
  );

  const supportSelectableIds = useMemo(
    () => supportSelectableCandidates.map((row) => supportCandidateKey(row)),
    [supportSelectableCandidates]
  );

  const supportAllVisibleSelected = useMemo(
    () => supportSelectableIds.length > 0 && supportSelectableIds.every((id) => supportSelectedIds.has(id)),
    [supportSelectableIds, supportSelectedIds]
  );

  const supportSomeVisibleSelected = useMemo(
    () => supportSelectableIds.some((id) => supportSelectedIds.has(id)),
    [supportSelectableIds, supportSelectedIds]
  );

  const dneSelectedRows = useMemo(
    () => dneRows.filter((row) => dneSelectedIds.has(row.id)),
    [dneRows, dneSelectedIds]
  );

  const dneSelectedRaiseIds = useMemo(
    () => dneSelectedRows
      .filter((row) => Number(row.age_days) >= 0 && Number(row.age_days) <= 30 && !DNE_AUTO_FINAL_STATUSES.includes(row.support_ticket_status))
      .map((row) => row.id),
    [dneSelectedRows]
  );

  const dneSelectedReraiseIds = useMemo(
    () => dneSelectedRows
      .filter((row) => Number(row.age_days) >= 0 && Number(row.age_days) <= 30 && isDneNotApproved(row))
      .map((row) => row.id),
    [dneSelectedRows]
  );

  const dneSelectedSubmittedRows = useMemo(
    () => dneSelectedRows.filter((row) => row.support_ticket_status === "SUBMITTED"),
    [dneSelectedRows]
  );

  const dneSelectedTrackableRows = useMemo(
    () => dneSelectedSubmittedRows.filter((row) => row.ticket_number && row.support_ticket_id),
    [dneSelectedSubmittedRows]
  );

  const dneSelectedTrackIds = useMemo(
    () => dneSelectedTrackableRows.map((row) => Number(row.support_ticket_id)).filter(Boolean),
    [dneSelectedTrackableRows]
  );

  const dneSelectedSubmittedWithoutTicketCount = useMemo(
    () => dneSelectedSubmittedRows.length - dneSelectedTrackableRows.length,
    [dneSelectedSubmittedRows, dneSelectedTrackableRows]
  );

  const dneSelectedSubmittedCount = dneSelectedSubmittedRows.length;

  const dneRaiseableIds = useMemo(
    () => dneRaiseableRows.map((row) => row.id),
    [dneRaiseableRows]
  );

  const dneSelectableIds = useMemo(
    () => dneSelectableRows.map((row) => row.id),
    [dneSelectableRows]
  );

  const dneAllVisibleSelected = useMemo(
    () => dneSelectableIds.length > 0 && dneSelectableIds.every((id) => dneSelectedIds.has(id)),
    [dneSelectableIds, dneSelectedIds]
  );

  const dneSomeVisibleSelected = useMemo(
    () => dneSelectableIds.some((id) => dneSelectedIds.has(id)),
    [dneSelectableIds, dneSelectedIds]
  );

  const claim60120RaiseableRows = useMemo(
    () => claim60120Rows.filter((row) => !DNE_FINAL_STATUSES.includes(row.support_ticket_status)),
    [claim60120Rows]
  );
  const claim60120SelectableRows = useMemo(
    () => claim60120Rows.filter((row) => !DNE_BLOCKED_SELECT_STATUSES.includes(row.support_ticket_status)),
    [claim60120Rows]
  );
  const claim60120SelectedRows = useMemo(
    () => claim60120Rows.filter((row) => claim60120SelectedIds.has(row.id)),
    [claim60120Rows, claim60120SelectedIds]
  );
  const claim60120SelectedRaiseIds = useMemo(
    () => claim60120SelectedRows.filter((row) => !DNE_FINAL_STATUSES.includes(row.support_ticket_status)).map((row) => row.id),
    [claim60120SelectedRows]
  );
  const claim60120SelectedSubmittedRows = useMemo(
    () => claim60120SelectedRows.filter((row) => row.support_ticket_status === "SUBMITTED"),
    [claim60120SelectedRows]
  );
  const claim60120SelectedTrackIds = useMemo(
    () => claim60120SelectedSubmittedRows
      .filter((row) => row.ticket_number && row.support_ticket_id)
      .map((row) => Number(row.support_ticket_id)).filter(Boolean),
    [claim60120SelectedSubmittedRows]
  );
  const claim60120RaiseableIds = useMemo(
    () => claim60120RaiseableRows.map((row) => row.id),
    [claim60120RaiseableRows]
  );
  const claim60120SelectableIds = useMemo(
    () => claim60120SelectableRows.map((row) => row.id),
    [claim60120SelectableRows]
  );
  const claim60120AllVisibleSelected = useMemo(
    () => claim60120SelectableIds.length > 0 && claim60120SelectableIds.every((id) => claim60120SelectedIds.has(id)),
    [claim60120SelectableIds, claim60120SelectedIds]
  );
  const claim60120SomeVisibleSelected = useMemo(
    () => claim60120SelectableIds.some((id) => claim60120SelectedIds.has(id)),
    [claim60120SelectableIds, claim60120SelectedIds]
  );

  const multiDimItems = useMemo(
    () => multiDimRows.flatMap((account) => (account.items || []).map((item) => ({
      ...item,
      account: account.account,
      accountOk: account.ok,
      accountError: account.error || "",
    }))),
    [multiDimRows]
  );
  const multiDimInterventionCount = useMemo(
    () => multiDimItems.filter((row) => row.needs_intervention).length,
    [multiDimItems]
  );

  const multiDimSafeCount = useMemo(
    () => multiDimItems.filter((row) => !row.needs_intervention).length,
    [multiDimItems]
  );

  const triggerMultiDimInterventionAlert = useCallback((count) => {
    if (!count) return;
    const text = `${count} Flipkart multi dimension row${count === 1 ? "" : "s"} need manual intervention`;
    setLatestAlert({ id: `multi-dims-${Date.now()}`, text });
    setAttentionAlert({ id: `multi-dims-${Date.now()}`, text });
  }, []);

  const triggerMultiDimFailureAlert = useCallback((failure) => {
    const unresolved = (failure?.unresolved || []).filter((row) => {
      const account = String(row.account || "");
      const orderId = String(row.order_id || "");
      const groupId = String(row.group_id || "");
      const accepted = (failure?.accounts || []).flatMap((item) => (item.items || []).map((candidate) => ({
        account: String(item.account || ""),
        order_id: String(candidate.order_id || ""),
        group_id: String(candidate.group_id || ""),
        accepted: candidate.accepted === true,
      }))).find((candidate) => candidate.accepted && candidate.account === account && (
        (orderId && candidate.order_id === orderId) ||
        (groupId && candidate.group_id === groupId)
      ));
      if (!accepted) return true;
      const failedTicket = (failure?.ticket_verification?.failures || []).some((ticket) =>
        String(ticket.account || ticket.account_name || "") === account &&
        String(ticket.order_id || "") === orderId
      ) || (failure?.ticket_queue?.results || []).some((ticket) =>
        !ticket.ok && String(ticket.account_name || "") === account &&
        String(ticket.order_id || "") === orderId
      );
      return failedTicket;
    });
    if (!unresolved.length) return;
    const identities = unresolved.slice(0, 4).map((row) =>
      `${row.account || "account"} ${row.order_id || row.group_id || row.sku || "row"}`
    ).join(", ");
    const count = unresolved.length || 1;
    const text = `Multi Dims failed after 3 retries for ${count} row${count === 1 ? "" : "s"}` +
      `${identities ? `: ${identities}` : ""}. Attention needed`;
    const alert = { id: `multi-dims-failed-${Date.now()}`, text };
    setLatestAlert(alert);
    setAttentionAlert(alert);
    setMessage(`ATTENTION — ${text}`);
    setMultiDimOpen(true);
  }, []);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(MULTI_DIM_DRAFT_KEY, JSON.stringify({
        rows: multiDimRows,
        result: multiDimResult,
        savedAt: new Date().toISOString(),
      }));
    } catch {
      // Keeping the live React state is still sufficient when storage is blocked.
    }
  }, [multiDimResult, multiDimRows]);

  const openMultiDimensions = useCallback(() => {
    setMultiDimOpen(true);
  }, []);

  const previewMultiDimensions = useCallback(async () => {
    if (!picked.length) {
      setMessage("Select account(s) first for Multi Dims.");
      return;
    }
    setMultiDimBusy(true);
    setMultiDimResult(null);
    setMessage("Checking selected Flipkart account(s) for multi orders missing dimensions…");
    try {
      const { data } = await api.post("/api/flipkart-console/multi-dimensions/preview", {
        accounts: picked,
      });
      setMultiDimRows(data.accounts || []);
      setMultiDimResult(data);
      const intervention = Number(data.needs_intervention || 0);
      if (intervention) triggerMultiDimInterventionAlert(intervention);
      setMessage(
        `Multi Dims preview ready — ${data.total || 0} missing multi order(s), ` +
        `${intervention} need manual intervention.`
      );
    } catch (err) {
      setMessage(err?.response?.data?.error || err?.message || "Could not preview missing multi dimensions.");
    } finally {
      setMultiDimBusy(false);
    }
  }, [picked, triggerMultiDimInterventionAlert]);

  const saveMultiDimensions = useCallback(async (confirmedRow = null) => {
    if (!picked.length) {
      setMessage("Select account(s) first for Multi Dims.");
      return;
    }
    const confirmedRetry = !!confirmedRow?.needs_weight_confirmation;
    if (!confirmedRetry && !multiDimSafeCount) {
      setMessage("No safe multi-dimension rows loaded. Click Find Missing Multi first.");
      return;
    }
    const proposedWeight = String(confirmedRow?.suggested_weight_kg || "");
    const ok = window.confirm(
      confirmedRetry
        ? `Retry only ${confirmedRow.order_id || confirmedRow.group_id || confirmedRow.sku}?\n\n` +
          `Previous weight: ${confirmedRow.previous_weight_kg || confirmedRow.auto_dimensions?.weight || "unknown"} kg\n` +
          `Proposed next slab: ${proposedWeight} kg\n\n` +
          `This order returned to To Accept. Continue only if you confirm this higher weight.`
        : `Process safe missing multi orders in selected account(s)?\n\n` +
          `Loaded now: ${multiDimSafeCount} safe, ${multiDimInterventionCount} need manual intervention.\n\n` +
          `For each safe row this will save dimensions, accept that exact order group, ` +
          `verify it left To Accept, and automatically raise its suspicious multi-quantity support ticket.`
    );
    if (!ok) return;
    const requestTargets = confirmedRetry ? [{
      account: confirmedRow.account,
      items: [{
        ...confirmedRow,
        confirmed_weight_kg: proposedWeight,
        weight_override_confirmed: true,
      }],
    }] : multiDimRows;
    setMultiDimSaving(true);
    setMultiDimRows((current) => current.map((account) => ({
      ...account,
      items: (account.items || []).map((row) => {
        const isConfirmedRow = confirmedRetry &&
          String(account.account || "") === String(confirmedRow.account || "") &&
          String(row.group_id || "") === String(confirmedRow.group_id || "");
        return row.needs_intervention && !isConfirmedRow ? row : {
          ...row,
          progress_stage: "queued",
          progress_message: isConfirmedRow
            ? `Confirmed retry at ${proposedWeight} kg`
            : "Waiting to process",
        };
      }),
    })));
    setMessage("Multi Dims started. Follow each row in the Status column.");
    try {
      const { data } = await api.post("/api/flipkart-console/multi-dimensions/save", {
        accounts: picked,
        targets: requestTargets,
      });
      const jobId = data.job_id;
      if (!jobId) throw new Error("Multi Dims job did not return a tracking ID");
      let finished = null;
      for (let attempt = 0; attempt < 1200; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const { data: status } = await api.get(`/api/flipkart-console/multi-dimensions/jobs/${jobId}`);
        const events = status.events || [];
        if (events.length) {
          setMultiDimRows((current) => current.map((account) => ({
            ...account,
            items: (account.items || []).map((row) => {
              const matches = events.filter((event) =>
                event.account === account.account && (
                  (event.group_id && event.group_id === String(row.group_id || "")) ||
                  (event.order_id && event.order_id === String(row.order_id || "")) ||
                  (!event.group_id && !event.order_id && event.sku === String(row.sku || "") && event.fsn === String(row.fsn || ""))
                )
              );
              const latest = matches[matches.length - 1];
              return latest ? {
                ...row,
                progress_stage: latest.stage,
                progress_message: latest.message,
                progress_at: latest.at,
              } : row;
            }),
          })));
        }
        if (status.attempt && status.max_attempts) {
          setMessage(`Multi Dims attempt ${status.attempt} of ${status.max_attempts}. Follow each row in the Status column.`);
        }
        if (status.status === "done" || status.status === "failed") {
          finished = status;
          break;
        }
      }
      if (!finished) throw new Error("Multi Dims is still running after twenty minutes");
      const result = finished.result;
      if (!result) throw new Error(finished.error || "Multi Dims failed without a result");
      const finalEvents = finished.events || [];
      setMultiDimRows((result.accounts || []).map((account) => ({
        ...account,
        items: (account.items || []).map((row) => {
          const matches = finalEvents.filter((event) =>
            event.account === account.account && (
              (event.group_id && event.group_id === String(row.group_id || "")) ||
              (event.order_id && event.order_id === String(row.order_id || "")) ||
              (!event.group_id && !event.order_id && event.sku === String(row.sku || "") && event.fsn === String(row.fsn || ""))
            )
          );
          const latest = matches[matches.length - 1];
          return latest ? {
            ...row,
            progress_stage: latest.stage,
            progress_message: latest.message,
            progress_at: latest.at,
          } : row;
        }),
      })));
      setMultiDimResult(result);
      const intervention = Number(result.needs_intervention || 0);
      if (intervention) triggerMultiDimInterventionAlert(intervention);
      const hasActionableFailure = (result.unresolved || []).some((row) => {
        const account = (result.accounts || []).find((item) => String(item.account || "") === String(row.account || ""));
        const accepted = (account?.items || []).some((item) => item.accepted === true && (
          (row.order_id && String(item.order_id || "") === String(row.order_id)) ||
          (row.group_id && String(item.group_id || "") === String(row.group_id))
        ));
        return !accepted;
      });
      if ((finished.status === "failed" || result.needs_attention) && hasActionableFailure) {
        triggerMultiDimFailureAlert(result);
      }
      const ticketQueue = result.ticket_queue || {};
      if (!hasActionableFailure) {
        setAttentionAlert((current) => current?.id?.startsWith("multi-dims-") ? null : current);
      }
      if (!hasActionableFailure) {
        setMessage(
          `Multi Dims processed — ${result.saved || 0} saved, ${result.accepted || 0} accepted, ` +
          `${ticketQueue.queued || 0} ticket(s) queued` +
          `${ticketQueue.skipped ? `, ${ticketQueue.skipped} existing ticket(s) skipped` : ""}` +
          `${ticketQueue.missing_identity ? `, ${ticketQueue.missing_identity} missing Order ID` : ""}; ` +
          `${intervention} need manual intervention.`
        );
      }
    } catch (err) {
      const data = err?.response?.data;
      if (data?.accounts) {
        setMultiDimRows(data.accounts);
        setMultiDimResult(data);
      }
      if (Number(data?.needs_intervention || 0)) {
        triggerMultiDimInterventionAlert(Number(data.needs_intervention || 0));
      }
      triggerMultiDimFailureAlert(data || { error: err?.message });
      const ticketQueue = data?.ticket_queue || {};
      setMessage(
        data?.accounts
          ? `Multi Dims partially processed — ${data.saved || 0} saved, ${data.accepted || 0} accepted, ` +
            `${ticketQueue.queued || 0} ticket(s) queued. Check row errors.`
          : (data?.error || err?.message || "Could not process missing multi dimensions.")
      );
    } finally {
      setMultiDimSaving(false);
    }
  }, [multiDimInterventionCount, multiDimRows, multiDimSafeCount, picked, triggerMultiDimFailureAlert, triggerMultiDimInterventionAlert]);

  const pollDneRun = useCallback((id, doneMessage) => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollRef.current = setInterval(async () => {
      try {
        const { data: st } = await api.get(`/api/flipkart-console/run/${id}`);
        if (st.log) {
          setRunLog(st.log);
          const runAttention = extractRunAttention(st.log);
          if (runAttention && runAttention.id !== lastRunAttentionRef.current) {
            lastRunAttentionRef.current = runAttention.id;
            setLatestAlert(runAttention);
            setAttentionAlert(runAttention);
            setMessage(`ATTENTION — ${runAttention.text}`);
            setLogOpen(true);
          }
        }
        if (st.done) {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
          setRunning(false);
          setDneBusy(false);
          setDneQueueing(false);
          setClaim60120Busy(false);
          setClaim60120Queueing(false);
          setSupportTracking(false);
          await loadDneReturns();
          await load60120Claims();
          await loadSupportTickets();
          playTaskCompleted();
          setTaskCompleteAlert(true);
          setMessage(doneMessage);
          setLogOpen(true);
        }
      } catch {
        /* keep polling */
      }
    }, 2000);
  }, [extractRunAttention, loadDneReturns, load60120Claims, loadSupportTickets, playTaskCompleted]);

  const previewDneReturns = useCallback(async () => {
    const allProfiles = accounts.map((account) => account.profile).filter(Boolean);
    const ok = window.confirm(
      "Fetch Flipkart returns marked 'Do not expect physical delivery' for ALL Flipkart accounts, last 90 days?"
    );
    if (!ok) return;
    setDneBusy(true);
    unlockCompletionAudio();
    setMessage("Queued DNE returns preview for all accounts, last 90 days...");
    setTaskCompleteAlert(false);
    setAttentionAlert(null);
    setLatestAlert(null);
    lastRunAttentionRef.current = "";
    setRunLog("Queued — fetching DNE returns for all Flipkart accounts from returns page/API.\n");
    setActiveRunOps(["returns_dne_candidates"]);
    setLogOpen(true);
    setRunning(true);
    try {
      const { data } = await api.post("/api/flipkart-console/support-tickets/returns-dne/preview", {
        accounts: allProfiles,
      });
      pollDneRun(data.id, "DNE returns preview complete — rows saved without duplicates.");
    } catch (err) {
      setRunning(false);
      setDneBusy(false);
      setMessage(err?.response?.data?.error || err?.message || "Could not queue DNE preview.");
    }
  }, [accounts, pollDneRun, unlockCompletionAudio]);

  const toggleDneSelected = useCallback((id) => {
    setDneSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleDneAllVisible = useCallback(() => {
    setDneSelectedIds((prev) => {
      const next = new Set(prev);
      if (dneAllVisibleSelected) {
        dneSelectableIds.forEach((id) => next.delete(id));
      } else {
        dneSelectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [dneAllVisibleSelected, dneSelectableIds]);

  const queueDneTicketIds = useCallback(async (ids, label = "selected") => {
    if (!ids.length) {
      setMessage("Select DNE return rows first.");
      return;
    }
    const ok = window.confirm(
      `Submit ${ids.length} Flipkart DNE/SPF claim(s) for ${label} row(s)?\n\n` +
      "The worker will complete and submit each claim, then read its exact status from SPF Claims."
    );
    if (!ok) return;
    setDneQueueing(true);
    unlockCompletionAudio();
    setMessage("Queued DNE/SPF claim submission…");
    setTaskCompleteAlert(false);
    setAttentionAlert(null);
    setLatestAlert(null);
    lastRunAttentionRef.current = "";
    setRunLog("Queued — submitting DNE/SPF claims and checking SPF status.\n");
    setActiveRunOps(["support_ticket"]);
    setLogOpen(true);
    setRunning(true);
    try {
      const { data } = await api.post("/api/flipkart-console/dne-returns/queue-tickets", { ids });
      if (data.skipped) {
        setMessage(data.message || "Selected DNE orders already have submitted claims.");
        setRunning(false);
        setDneQueueing(false);
        await loadDneReturns();
        await loadSupportTickets();
        return;
      }
      pollDneRun(data.id, "DNE/SPF claim run complete — tracker refreshed.");
    } catch (err) {
      setRunning(false);
      setDneQueueing(false);
      setMessage(err?.response?.data?.error || err?.message || "Could not queue DNE/SPF claims.");
    }
  }, [loadDneReturns, loadSupportTickets, pollDneRun, unlockCompletionAudio]);

  const queueDneTickets = useCallback(
    () => queueDneTicketIds(dneSelectedRaiseIds, "selected"),
    [dneSelectedRaiseIds, queueDneTicketIds]
  );

  const reraiseDneTickets = useCallback(async () => {
    if (!dneSelectedReraiseIds.length) {
      setMessage("Select DNE rows with Not Approved status first.");
      return;
    }
    const ok = window.confirm(
      `Reraise ${dneSelectedReraiseIds.length} Not Approved DNE claim(s) as fresh Flipkart tickets?\n\n` +
      "Previous incident IDs and replies will be preserved in history. The worker will submit fresh claims and verify their SPF status."
    );
    if (!ok) return;
    setDneQueueing(true);
    unlockCompletionAudio();
    setMessage("Queued fresh DNE/SPF claim submission…");
    setTaskCompleteAlert(false);
    setAttentionAlert(null);
    setLatestAlert(null);
    lastRunAttentionRef.current = "";
    setRunLog("Queued — reraising Not Approved DNE/SPF claims as fresh tickets.\n");
    setActiveRunOps(["support_ticket"]);
    setLogOpen(true);
    setRunning(true);
    try {
      const { data } = await api.post("/api/flipkart-console/dne-returns/reraise-tickets", {
        ids: dneSelectedReraiseIds,
      });
      pollDneRun(data.id, "Fresh DNE/SPF claim submission complete — tracker refreshed.");
    } catch (err) {
      setRunning(false);
      setDneQueueing(false);
      setMessage(err?.response?.data?.error || err?.message || "Could not reraise DNE/SPF claims.");
    }
  }, [dneSelectedReraiseIds, pollDneRun, unlockCompletionAudio]);

  const queueAllVisibleDneTickets = useCallback(
    () => queueDneTicketIds(dneRaiseableIds, "visible open"),
    [dneRaiseableIds, queueDneTicketIds]
  );

  const trackDneTicketStatus = useCallback(async () => {
    if (!dneSelectedIds.size) {
      setMessage("Select submitted DNE/SPF rows first.");
      return;
    }
    if (!dneSelectedSubmittedRows.length) {
      const noTicketCount = dneSelectedRows.filter((row) => row.support_ticket_status === "SUBMITTED_NO_TICKET").length;
      setMessage(noTicketCount
        ? `${noTicketCount} selected DNE/SPF claim(s) were submitted but no Flipkart ticket number was captured. Verify in Flipkart before retrying.`
        : "Selected DNE rows are not submitted claims yet.");
      return;
    }
    if (!dneSelectedTrackIds.length) {
      setMessage(
        `${dneSelectedSubmittedWithoutTicketCount} selected submitted DNE/SPF claim(s) have no Flipkart ticket number yet, so no status can be tracked.`
      );
      return;
    }
    const ok = window.confirm(
      `Track Flipkart status for ${dneSelectedTrackIds.length} selected submitted DNE/SPF claim(s)?\n\n` +
      (dneSelectedSubmittedWithoutTicketCount
        ? `${dneSelectedSubmittedWithoutTicketCount} selected submitted claim(s) have no Flipkart ticket number yet and will be skipped.\n\n`
        : "") +
      "This opens the seller support ticket area in each account browser and saves the visible status/reply."
    );
    if (!ok) return;
    setSupportTracking(true);
    unlockCompletionAudio();
    setMessage("Queued DNE/SPF status tracking…");
    setTaskCompleteAlert(false);
    setAttentionAlert(null);
    setLatestAlert(null);
    lastRunAttentionRef.current = "";
    setRunLog("Queued — tracking DNE/SPF claim status/replies.\n");
    setActiveRunOps(["support_ticket_track"]);
    setLogOpen(true);
    setRunning(true);
    try {
      const { data } = await api.post("/api/flipkart-console/support-tickets/track", {
        ids: dneSelectedTrackIds,
      });
      if (data.skipped) {
        setMessage(data.message || "No submitted DNE/SPF claims to track.");
        setRunning(false);
        setSupportTracking(false);
        return;
      }
      pollDneRun(data.id, "DNE/SPF status tracking complete — tracker refreshed.");
    } catch (err) {
      setRunning(false);
      setSupportTracking(false);
      setMessage(err?.response?.data?.error || err?.message || "Could not queue DNE/SPF status tracking.");
    }
  }, [
    dneSelectedIds.size, dneSelectedSubmittedRows.length, dneSelectedTrackIds,
    dneSelectedRows, dneSelectedSubmittedWithoutTicketCount, pollDneRun, unlockCompletionAudio,
  ]);

  const preview60120Claims = useCallback(async () => {
    const selectedProfiles = [...new Set(picked)];
    if (!selectedProfiles.length) {
      setMessage("Tick at least one Flipkart account for the 61-120 claim preview.");
      return;
    }
    const selectedNames = accounts
      .filter((account) => selectedProfiles.includes(account.profile))
      .map((account) => account.name);
    const ok = window.confirm(
      `Fetch only In Transit Flipkart returns aged 61 through 120 completed days for ${selectedNames.length} selected account(s)?\n\n${selectedNames.join(", ")}`
    );
    if (!ok) return;
    setClaim60120Busy(true);
    unlockCompletionAudio();
    setMessage(`Queued 61-120 return claim preview for ${selectedNames.length} selected account(s)...`);
    setTaskCompleteAlert(false);
    setRunLog("Queued — selecting the exact 61-120 day range and fetching In Transit Flipkart returns.\n");
    setActiveRunOps(["returns_60_120_candidates"]);
    setLogOpen(true);
    setRunning(true);
    try {
      const { data } = await api.post("/api/flipkart-console/support-tickets/returns-60-120/preview", {
        accounts: selectedProfiles,
      });
      pollDneRun(data.id, "61-120 return preview complete — rows saved without duplicates.");
    } catch (err) {
      setRunning(false);
      setClaim60120Busy(false);
      setMessage(err?.response?.data?.error || err?.message || "Could not queue 60-120 claim preview.");
    }
  }, [accounts, picked, pollDneRun, unlockCompletionAudio]);

  const toggle60120Selected = useCallback((id) => {
    setClaim60120SelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggle60120AllVisible = useCallback(() => {
    setClaim60120SelectedIds((prev) => {
      const next = new Set(prev);
      if (claim60120AllVisibleSelected) claim60120SelectableIds.forEach((id) => next.delete(id));
      else claim60120SelectableIds.forEach((id) => next.add(id));
      return next;
    });
  }, [claim60120AllVisibleSelected, claim60120SelectableIds]);

  const queue60120TicketIds = useCallback(async (ids, label = "selected") => {
    if (!ids.length) {
      setMessage("Select 61-120 return rows first.");
      return;
    }
    const ok = window.confirm(
      `Submit Flipkart post-60-day claims for ${ids.length} ${label} row(s)?\n\n` +
      "The worker will batch up to 15 Order Item IDs, upload the official template, click Get Status, " +
      "then click Submit Ticket for the eligible IDs and create a real Flipkart ticket."
    );
    if (!ok) return;
    setClaim60120Queueing(true);
    unlockCompletionAudio();
    setMessage("Queued post-60-day claim submission...");
    setRunLog("Queued — uploading the post-60-day claim template and submitting eligible IDs.\n");
    setActiveRunOps(["support_ticket"]);
    setLogOpen(true);
    setRunning(true);
    try {
      const { data } = await api.post("/api/flipkart-console/claims-60-120/queue-tickets", { ids });
      if (data.skipped) {
        setMessage(data.message || "Selected 60-120 claims are already submitted.");
        setRunning(false);
        setClaim60120Queueing(false);
        await load60120Claims();
        return;
      }
      pollDneRun(data.id, "Post-60-day claim submission complete — Flipkart ticket results refreshed.");
    } catch (err) {
      setRunning(false);
      setClaim60120Queueing(false);
      setMessage(err?.response?.data?.error || err?.message || "Could not queue 60-120 claims.");
    }
  }, [load60120Claims, pollDneRun, unlockCompletionAudio]);

  const queue60120Tickets = useCallback(
    () => queue60120TicketIds(claim60120SelectedRaiseIds, "selected"),
    [claim60120SelectedRaiseIds, queue60120TicketIds]
  );
  const queueAllVisible60120Tickets = useCallback(
    () => queue60120TicketIds(claim60120RaiseableIds, "visible open"),
    [claim60120RaiseableIds, queue60120TicketIds]
  );

  const track60120TicketStatus = useCallback(async () => {
    if (!claim60120SelectedTrackIds.length) {
      setMessage("Select submitted 60-120 claims with ticket numbers first.");
      return;
    }
    const ok = window.confirm(`Track Flipkart status for ${claim60120SelectedTrackIds.length} selected 60-120 claim(s)?`);
    if (!ok) return;
    setSupportTracking(true);
    setMessage("Queued 60-120 claim status tracking...");
    setRunLog("Queued — tracking 61-120 return claim status/replies.\n");
    setActiveRunOps(["support_ticket_track"]);
    setLogOpen(true);
    setRunning(true);
    try {
      const { data } = await api.post("/api/flipkart-console/support-tickets/track", {
        ids: claim60120SelectedTrackIds,
      });
      if (data.skipped) {
        setMessage(data.message || "No submitted 60-120 claims to track.");
        setRunning(false);
        setSupportTracking(false);
        return;
      }
      pollDneRun(data.id, "60-120 claim status tracking complete — tracker refreshed.");
    } catch (err) {
      setRunning(false);
      setSupportTracking(false);
      setMessage(err?.response?.data?.error || err?.message || "Could not track 60-120 claims.");
    }
  }, [claim60120SelectedTrackIds, pollDneRun]);


  const fetchPdf = useCallback(
    async (asDownload) => {
      if (!pdf) return;
      try {
        const isExcel = isExcelArtifact(pdf);
        const { data } = await api.get("/api/archive/file", {
          params: { p: pdf.relpath, ...((asDownload || isExcel) ? { dl: 1 } : {}) },
          responseType: "blob",
        });
        const url = URL.createObjectURL(new Blob([data], { type: artifactMimeType(pdf) }));
        if (asDownload || isExcel) {
          const a = document.createElement("a");
          a.href = url;
          a.download = artifactDownloadName(pdf);
          document.body.appendChild(a);
          a.click();
          a.remove();
        } else {
          // New tab = the browser's own PDF viewer, so Ctrl/Cmd-P reaches the
          // printer attached to THIS machine.
          const w = window.open(url, "_blank", "noopener");
          if (!w) {
            // popup blocked (async open, no user gesture) — the "Open & Print"
            // button below is the manual fallback.
            setMessage("Labels PDF ready — click “Open & Print” (your browser blocked the auto-open popup).");
          }
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (err) {
        setMessage(err?.response?.data?.error || err?.message || "Could not fetch the file.");
      }
    },
    [pdf]
  );

  useEffect(() => {
    let cancelled = false;
    if (pdf && pdf.relpath && autoOpenedRef.current !== pdf.relpath) {
      autoOpenedRef.current = pdf.relpath;   // fetch each new artifact exactly once
      if (isExcelArtifact(pdf)) return;
      (async () => {
        try {
          const { data } = await api.get("/api/archive/file", {
            params: { p: pdf.relpath },
            responseType: "blob",
          });
          const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
          if (cancelled) { URL.revokeObjectURL(url); return; }
          setPdfPreviewVisible(true);
          setAutoPdfUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
        } catch {
          /* the Open & Print button stays as the manual fallback */
        }
      })();
    }
    return () => { cancelled = true; };
  }, [pdf]);

  // Scheduled runs finish outside this tab's manual-run poll. Poll the newest
  // actual PDF artifact (ignoring later counts-only results) and surface it here.
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get("/api/flipkart-console/last-labels");
        if (!cancelled && data && data.pdf) {
          const seen = sessionStorage.getItem("fk_last_print_ready") || "";
          const next = { relpath: data.pdf, name: data.pdfName, pages: data.pages };
          setPdf(next);
          if (seen !== data.pdf) {
            sessionStorage.setItem("fk_last_print_ready", data.pdf);
            setPrintReady(true);
            playTaskCompleted();
            setTaskCompleteAlert(true);
            setMessage(`PRINT READY — ${data.pages || "label"} PDF page(s). Click Open & Print.`);
            setLogOpen(true);
          }
        }
      } catch {
        /* no recent labels — nothing to show */
      }
    };
    check();
    const timer = setInterval(check, 5000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [playTaskCompleted]);

  useEffect(() => {
    let cancelled = false;
    const isWatchdogAlert = (alert) => {
      const text = String(alert?.text || alert?.id || "");
      return alert?.source === "watchdog_alert"
        || /SCHEDULE MISSED|COUNTS UNREADABLE|PENDING RTD STILL LEFT|SERVER STALE|LOGIN\/PROFILE PROBLEM|FLIPKART LOGIN REQUIRED/i.test(text);
    };
    const pollAlerts = async () => {
      try {
        const { data } = await api.get("/api/flipkart-console/watchdog-alerts");
        const alerts = data?.alerts || [];
        const alert = alerts[alerts.length - 1];
        if (cancelled) return;
        if (!alert) {
          setLatestAlert((prev) => (isWatchdogAlert(prev) ? null : prev));
          setAttentionAlert((prev) => (isWatchdogAlert(prev) ? null : prev));
          return;
        }
        const seen = localStorage.getItem("fk_last_spoken_alert") || "";
        if (alert.id === seen) return;
        localStorage.setItem("fk_last_spoken_alert", alert.id);
        const tagged = { ...alert, source: "watchdog_alert" };
        setLatestAlert(tagged);
        setAttentionAlert(tagged);
        setMessage(`ATTENTION — ${alert.text}`);
      } catch { /* keep polling */ }
    };
    pollAlerts();
    const timer = setInterval(pollAlerts, 5000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  const loadAccounts = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/flipkart-console/accounts?t=${Date.now()}`);
      const list = data?.accounts || [];
      setAccounts(list);
      // default to every account whose browser is actually up
      setPicked((prev) =>
        prev.length ? prev : list.filter((a) => a.running).map((a) => a.profile)
      );
    } catch {
      setAccounts([]); // non-fatal: console still usable without the switcher
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const switchTo = useCallback(
    async (profile) => {
      setSwitching(profile);
      setMessage("");
      try {
        await api.post("/api/flipkart-console/switch", { profile });
        setActive(profile);
        loadAccounts(); // refresh running flags
      } catch (err) {
        setMessage(
          err?.response?.data?.error || err?.message || `Could not switch to ${profile}.`
        );
      } finally {
        setSwitching("");
      }
    },
    [loadAccounts]
  );

  const toggle = (list, setList, key) =>
    setList(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);

  const flowSelected = FLOW_KEYS.every((key) => ops.includes(key));
  const toggleFlow = () => {
    setOps((prev) => {
      if (FLOW_KEYS.every((key) => prev.includes(key))) {
        return prev.filter((key) => !FLOW_KEYS.includes(key));
      }
      return [...prev, ...FLOW_KEYS.filter((key) => !prev.includes(key))];
    });
  };
  const resolveOps = useCallback((rawOps) => {
    const resolved = [];
    (rawOps || []).forEach((op) => {
      let actual = op;
      if (op === "accept") actual = flowScope === "all" ? "accept" : "accept_today";
      if (op === "print") actual = flowScope === "all" ? "print" : "print_today";
      if (op === "rtd") actual = flowScope === "all" ? "rtd_all" : "rtd";
      if (!resolved.includes(actual)) resolved.push(actual);
    });
    return resolved;
  }, [flowScope]);

  const loadSchedule = useCallback(async () => {
    try {
      const { data } = await api.get("/api/flipkart-console/schedule");
      setSchedule(data);
    } catch (err) {
      setMessage(err?.response?.data?.error || "Could not read scheduler status.");
    }
  }, []);

  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const toggleSchedule = useCallback(async () => {
    setScheduleBusy(true);
    try {
      const { data } = await api.post("/api/flipkart-console/schedule/toggle", {
        enabled: schedule.enabled && schedule.status === "online" ? false : true,
      });
      setSchedule(data);
    } catch (err) {
      setMessage(err?.response?.data?.error || "Could not change scheduler status.");
    } finally {
      setScheduleBusy(false);
    }
  }, [schedule.enabled, schedule.status]);

  const openScheduleSummary = useCallback(async () => {
    setScheduleOpen(true);
    try {
      const { data } = await api.get("/api/flipkart-console/schedule/summary");
      setSchedule(data);
      setScheduleRuns(data.runs || []);
    } catch (err) {
      setMessage(err?.response?.data?.error || "Could not load run summary.");
    }
  }, []);

  const openScheduleSettings = useCallback(() => {
    const rows = (schedule.schedules || []).length
      ? schedule.schedules
      : (schedule.times || []).map((time) => ({ time, scope: "today", operations: ["accept", "print", "rtd"] }));
    setEditSchedules(rows.map((row) => ({
      time: row.time,
      scope: row.scope === "all" ? "all" : "today",
      operations: row.operations || ["accept", "print", "rtd", "upload"],
    })));
    setScheduleSettingsOpen(true);
  }, [schedule.schedules, schedule.times]);

  const saveScheduleTimes = useCallback(async () => {
    setScheduleBusy(true);
    try {
      const schedules = editSchedules.map((row) => ({
        ...row,
      }));
      const { data } = await api.put("/api/flipkart-console/schedule/times", { schedules });
      setSchedule((old) => ({ ...old, times: data.times || [], schedules: data.schedules || [] }));
      setScheduleSettingsOpen(false);
    } catch (err) {
      setMessage(err?.response?.data?.error || "Could not save schedule times.");
    } finally {
      setScheduleBusy(false);
    }
  }, [editSchedules]);

  // ── running ops ───────────────────────────────────────────────────────────
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const runSelected = useCallback(async (modeArg, opsOverride) => {
    const mode = typeof modeArg === "string" ? modeArg : null;
    const selectedOps = Array.isArray(opsOverride) ? opsOverride : resolveOps(ops);
    // Return OTC needs a direction — ask START or END first (unless already picked).
    if (selectedOps.includes("return_otc") && !mode) {
      setOtcPickOpen(true);
      return;
    }
    // RTD All Dates dispatches FUTURE-dated orders early — confirm before running.
    if (selectedOps.includes("rtd_all")) {
      const ok = window.confirm(
        `RTD ALL DATES will mark EVERY Pending-RTD order — today, tomorrow AND later dates — ` +
        `Ready-to-Dispatch on ${picked.length} account(s).\n\n` +
        `This dispatches future-dated orders early. Continue?`
      );
      if (!ok) return;
    }
    if (selectedOps.includes("scan_rtd_all")) {
      const ok = window.confirm(
        `ALL SCAN RTD will take every uploaded tracking ID from today's Scan upload ` +
        `for ${picked.length} account(s), even if packets were not scanned yet, and submit them to Flipkart Scan RTD across all visible Pending-RTD dates.\n\n` +
        `Only use this for RTD recovery/cross-check runs. Continue?`
      );
      if (!ok) return;
    }
    unlockCompletionAudio();
    setMessage("");
    setReturnOtc(null);
    setTaskCompleteAlert(false);
    setAttentionAlert(null);
    setLatestAlert(null);
    lastRunAttentionRef.current = "";
    setRunLog("Queued — waiting for the runner…\n");
    setActiveRunOps(selectedOps);
    setLogOpen(true);
    setRunning(true);
    setPdf(null);                       // last run's labels are not this run's
    setAutoPdfUrl((old) => { if (old) URL.revokeObjectURL(old); return null; });
    try {
      const { data } = await api.post("/api/flipkart-console/run", {
        accounts: picked,
        ops: selectedOps,
        mode: mode || "start",
      });
      const id = data.id;
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/api/flipkart-console/run/${id}`);
          if (st.log) {
            setRunLog(st.log);
            const runAttention = extractRunAttention(st.log);
            if (runAttention && runAttention.id !== lastRunAttentionRef.current) {
              lastRunAttentionRef.current = runAttention.id;
              setLatestAlert(runAttention);
              setAttentionAlert(runAttention);
              setMessage(`ATTENTION — ${runAttention.text}`);
              setLogOpen(true);
            }
          }
          if (st.pdf) setPdf({ relpath: st.pdf, name: st.pdfName });
          else if (st.skuList?.relpath) setPdf({ relpath: st.skuList.relpath, name: st.skuList.name });
          if (st.returnOtc) setReturnOtc(st.returnOtc);
          if (st.done) {
            stopPolling();
            setRunning(false);
            setTaskCompleteAlert(true);
            setMessage("TASK COMPLETED.");
            setLogOpen(true);
          }
        } catch {
          /* keep polling; a blip shouldn't kill the view of a live run */
        }
      }, 2000);
    } catch (err) {
      setRunning(false);
      setRunLog("");
      setLogOpen(false);
      setMessage(err?.response?.data?.error || err?.message || "Could not start the run.");
    }
  }, [extractRunAttention, picked, ops, resolveOps, stopPolling, unlockCompletionAudio]);

  const autoLoginSelected = useCallback(async () => {
    if (!picked.length) {
      setMessage("Tick at least one Flipkart account for Auto Login.");
      return;
    }
    if (running || serverBusy) {
      setMessage("Flipkart runner is already busy.");
      return;
    }
    unlockCompletionAudio();
    setMessage("");
    setReturnOtc(null);
    setTaskCompleteAlert(false);
    setAttentionAlert(null);
    setLatestAlert(null);
    lastRunAttentionRef.current = "";
    setRunLog("Queued — auto-login/read-only count check for selected Flipkart account(s).\n");
    setActiveRunOps(["auto-login"]);
    setLogOpen(true);
    setRunning(true);
    setPdf(null);
    setAutoPdfUrl((old) => { if (old) URL.revokeObjectURL(old); return null; });
    try {
      const { data } = await api.post("/api/flipkart-console/auto-login", {
        accounts: picked,
      });
      const id = data.id;
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/api/flipkart-console/run/${id}`);
          if (st.log) setRunLog(st.log);
          if (st.done) {
            stopPolling();
            setRunning(false);
            setTaskCompleteAlert(true);
            setMessage("AUTO LOGIN CHECK COMPLETED.");
            setLogOpen(true);
            loadAccounts();
          }
        } catch {
          /* keep polling; the runner may still be writing the first log lines */
        }
      }, 2000);
    } catch (err) {
      setRunning(false);
      setRunLog("");
      setLogOpen(false);
      setMessage(err?.response?.data?.error || err?.message || "Could not start Auto Login.");
    }
  }, [loadAccounts, picked, running, serverBusy, stopPolling, unlockCompletionAudio]);

  const logoutSelected = useCallback(async () => {
    const selectedProfiles = [...new Set(picked)];
    if (!selectedProfiles.length) {
      setMessage("Tick at least one Flipkart account to Logout.");
      return;
    }
    if (running || serverBusy) {
      setMessage("Flipkart runner is already busy.");
      return;
    }
    const selectedNames = accounts
      .filter((account) => selectedProfiles.includes(account.profile))
      .map((account) => account.name);
    const ok = window.confirm(
      `Log out ${selectedNames.length} selected Flipkart account(s)?\n\n` +
      `${selectedNames.join(", ")}\n\nEach selected account will be logged out once in parallel.`
    );
    if (!ok) return;

    setMessage("");
    setTaskCompleteAlert(false);
    setRunLog(`Queued — logging out ${selectedNames.length} selected Flipkart account(s) once in parallel.\n`);
    setActiveRunOps(["logout"]);
    setLogOpen(true);
    setRunning(true);
    try {
      const { data } = await api.post("/api/flipkart-console/logout", {
        accounts: selectedProfiles,
      });
      const id = data.id;
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/api/flipkart-console/run/${id}`);
          if (st.log) setRunLog(st.log);
          if (st.done) {
            stopPolling();
            setRunning(false);
            setTaskCompleteAlert(true);
            setMessage(`LOGOUT COMPLETED FOR ${data.count || selectedNames.length} ACCOUNT(S).`);
            setLogOpen(true);
            loadAccounts();
          }
        } catch {
          /* keep polling while the pool writes the logout result */
        }
      }, 2000);
    } catch (err) {
      setRunning(false);
      setRunLog("");
      setLogOpen(false);
      setMessage(err?.response?.data?.error || err?.message || "Could not start Logout.");
    }
  }, [accounts, loadAccounts, picked, running, serverBusy, stopPolling]);

  const stopRun = useCallback(async () => {
    const ok = window.confirm(
      "Stop the running Flipkart batch?\n\n" +
      "This clears anything still queued and halts the step that is mid-flight. " +
      "Work already finished (orders accepted, labels downloaded) is kept, but a " +
      "step that is only part-way through will not complete.\n\n" +
      "The seven browsers stay open and logged in."
    );
    if (!ok) return;
    setStopping(true);
    try {
      const { data } = await api.post("/api/flipkart-console/stop");
      stopPolling();
      setRunning(false);
      setServerBusy(false);
      setRunLog((old) => (old || "") +
        `\n— STOPPED by user (${data.cleared || 0} queued job(s) cleared) —\n`);
      setMessage(data.recoveryQueued
        ? "Run stopped. Downloaded labels queued for merge/crop recovery."
        : "Run stopped. Browsers are still logged in.");
    } catch (err) {
      setMessage(err?.response?.data?.error || "Could not stop the run.");
    } finally {
      setStopping(false);
    }
  }, [stopPolling]);

  // Poll the runner for live work so STOP shows regardless of who started it.
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/api/flipkart-console/active");
        if (!stop) setServerBusy(!!data.active);
      } catch {
        /* a blip shouldn't flip the button */
      }
    };
    tick();
    const t = setInterval(tick, 3000);
    return () => { stop = true; clearInterval(t); };
  }, []);

  const clampRunDialogPos = useCallback((x, y, width = 720, height = 420) => {
    const margin = 8;
    const minVisibleX = Math.min(160, Math.max(80, width / 3));
    const minVisibleY = 80;
    const maxX = Math.max(margin, window.innerWidth - minVisibleX);
    const maxY = Math.max(margin, window.innerHeight - minVisibleY);
    return {
      x: Math.min(Math.max(margin, x), maxX),
      y: Math.min(Math.max(margin, y), maxY),
    };
  }, []);

  const clampRunDialogSize = useCallback((width, height) => {
    const margin = 8;
    return {
      width: Math.min(Math.max(420, width), Math.max(420, window.innerWidth - margin * 2)),
      height: Math.min(Math.max(260, height), Math.max(260, window.innerHeight - margin * 2)),
    };
  }, []);

  const stopRunDialogDrag = useCallback(() => {
    const drag = runDialogDragRef.current;
    runDialogDragRef.current = null;
    if (drag?.move) window.removeEventListener("pointermove", drag.move);
    window.removeEventListener("pointerup", stopRunDialogDrag);
    window.removeEventListener("pointercancel", stopRunDialogDrag);
  }, []);

  const stopRunDialogResize = useCallback(() => {
    const resize = runDialogResizeRef.current;
    runDialogResizeRef.current = null;
    if (resize?.move) window.removeEventListener("pointermove", resize.move);
    if (resize?.stop) {
      window.removeEventListener("pointerup", resize.stop);
      window.removeEventListener("pointercancel", resize.stop);
    }
  }, []);

  const startRunDialogResize = useCallback((event) => {
    if (event.button !== 0) return;
    const paper = event.currentTarget.closest(".MuiPaper-root");
    if (!paper) return;
    const rect = paper.getBoundingClientRect();
    const resize = {
      startX: event.clientX,
      startY: event.clientY,
      width: rect.width,
      height: rect.height,
      move: (moveEvent) => {
        const current = runDialogResizeRef.current;
        if (!current) return;
        setRunDialogSize(clampRunDialogSize(
          current.width + moveEvent.clientX - current.startX,
          current.height + moveEvent.clientY - current.startY
        ));
      },
      stop: stopRunDialogResize,
    };
    runDialogResizeRef.current = resize;
    setRunDialogSize(clampRunDialogSize(rect.width, rect.height));
    window.addEventListener("pointermove", resize.move);
    window.addEventListener("pointerup", resize.stop);
    window.addEventListener("pointercancel", resize.stop);
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* best effort */ }
    event.preventDefault();
    event.stopPropagation();
  }, [clampRunDialogSize, stopRunDialogResize]);

  const startRunDialogDrag = useCallback((event) => {
    if (event.button !== 0) return;
    if (event.target.closest("button,a,input,textarea,select,[role='button']")) return;
    const paper = event.currentTarget.closest(".MuiPaper-root");
    if (!paper) return;
    const rect = paper.getBoundingClientRect();
    const drag = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      move: (moveEvent) => {
        const current = runDialogDragRef.current;
        if (!current) return;
        setRunDialogPos(clampRunDialogPos(
          moveEvent.clientX - current.offsetX,
          moveEvent.clientY - current.offsetY,
          current.width,
          current.height
        ));
      },
    };
    runDialogDragRef.current = drag;
    setRunDialogPos(clampRunDialogPos(rect.left, rect.top, rect.width, rect.height));
    window.addEventListener("pointermove", drag.move);
    window.addEventListener("pointerup", stopRunDialogDrag);
    window.addEventListener("pointercancel", stopRunDialogDrag);
    event.preventDefault();
  }, [clampRunDialogPos, stopRunDialogDrag]);

  useEffect(() => () => {
    stopRunDialogDrag();
    stopRunDialogResize();
  }, [stopRunDialogDrag, stopRunDialogResize]);

  const cleanup = useCallback(() => {
    if (rfbRef.current) {
      try {
        rfbRef.current.disconnect();
      } catch {
        /* ignore */
      }
      rfbRef.current = null;
    }
    setStatus((prev) => (prev === "connected" ? "idle" : prev));
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get("/api/flipkart-console/status")
      .then(({ data }) => {
        if (!cancelled) setConsoleMode(data?.mode === "mac" ? "mac" : "remote");
      })
      .catch(() => {
        // Keep the remote default when the status probe is unavailable.
      });
    return () => { cancelled = true; };
  }, []);

  const connect = useCallback(async () => {
    cleanup();
    setMessage("");
    setStatus("connecting");
    try {
      if (consoleMode === "mac") {
        setStatus("connected");
        setMessage("Mac Chrome mode active. Use an account button to show its real Chrome window.");
        return;
      }
      // 60s single-use ticket; permission-checked server-side.
      const { data } = await api.get("/api/flipkart-console/ticket");
      const url = `${wsOrigin()}${data.wsPath || "/flipkart-ws/"}?ticket=${encodeURIComponent(
        data.ticket
      )}`;
      const rfb = new RFB(screenRef.current, url, {});
      rfb.viewOnly = false;
      rfb.focusOnClick = true;
      rfb.scaleViewport = true;
      rfb.resizeSession = true;
      rfb.clipViewport = false;
      rfb.background = "#0b0b0b";
      rfb.addEventListener("connect", () => setStatus("connected"));
      rfb.addEventListener("disconnect", (e) => {
        setStatus(e?.detail?.clean ? "closed" : "error");
        if (!e?.detail?.clean) setMessage("Connection dropped. Reconnect to retry.");
      });
      rfb.addEventListener("securityfailure", (e) => {
        setStatus("error");
        setMessage(e?.detail?.reason || "Access denied.");
      });
      rfb.addEventListener("clipboard", (e) => {
        const text = String(e?.detail?.text || "");
        if (!text) return;
        setRemoteClip(text);
        copyRemoteToLocal(text);
      });
      rfbRef.current = rfb;
    } catch (err) {
      setStatus("error");
      setMessage(
        err?.response?.status === 403
          ? "You do not have access to the Flipkart Console."
          : err?.response?.data?.error || err?.message || "Could not start the session."
      );
    }
  }, [cleanup, consoleMode, copyRemoteToLocal]);

  const fullscreen = useCallback(() => {
    const el = screenRef.current;
    if (el?.requestFullscreen) el.requestFullscreen();
  }, []);

  useEffect(() => () => {
    cleanup();
    stopPolling();
  }, [cleanup, stopPolling]);

  const s = STATUS[status] || STATUS.idle;

  // This page sits on the admin theme's blue background, so its own text has to
  // be white -- the default primary blue is nearly invisible against it.
  const INK = "#ffffff";
  const railBtn = (isActive) => ({
    textTransform: "none",
    justifyContent: "flex-start",
    px: 1,
    minWidth: 0,
    whiteSpace: "nowrap",
    lineHeight: 1.15,
    color: isActive ? "primary.main" : INK,
    bgcolor: isActive ? INK : "transparent",
    borderColor: "rgba(255,255,255,0.55)",
    "&:hover": {
      bgcolor: isActive ? INK : "rgba(255,255,255,0.14)",
      borderColor: INK,
    },
    "&.Mui-disabled": { color: "rgba(255,255,255,0.4)", borderColor: "rgba(255,255,255,0.2)" },
  });
  const tick = {
    color: "rgba(255,255,255,0.7)",
    p: 0.25,
    "&.Mui-checked": { color: INK },
  };
  const tickLabel = {
    m: 0,
    "& .MuiFormControlLabel-label": {
      fontSize: 11.5,
      color: INK,
      lineHeight: 1.2,
      whiteSpace: "nowrap",
    },
  };

  return (
    // Side rails instead of stacked header rows: the remote browser is the point
    // of this page. Keep the rails compact and let noVNC resize into all
    // remaining space so the console does not sit tiny between the menus.
    <Box
      sx={{
        p: 0,
        display: "flex",
        flexWrap: { xs: "wrap", md: "nowrap" },
        gap: { xs: 0.5, md: 0.25 },
        height: { xs: "auto", md: "calc(100vh - 56px)" },
        alignItems: "stretch",
        color: INK,
      }}
    >
      {/* LEFT RAIL — console header + connection controls, then the accounts */}
      <Stack spacing={0.4} sx={{
        order: { xs: 1, md: 0 },
        width: { xs: "calc(50% - 4px)", md: 148 },
        flexShrink: 0,
        color: INK,
        maxHeight: { xs: "34vh", md: "none" },
        overflowY: "auto",
      }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <FaDesktop />
          <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
            Flipkart Console
          </Typography>
        </Stack>
        <Chip
          size="small"
          label={s.label}
          color={s.color}
          sx={{ width: "100%", fontSize: 11, ...(s.color === "default" ? { color: INK } : {}) }}
        />
        {status === "connected" ? (
          <Button
            size="small"
            fullWidth
            variant="outlined"
            startIcon={<FaPowerOff />}
            onClick={cleanup}
            sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
          >
            Disconnect
          </Button>
        ) : (
          <Button
            size="small"
            fullWidth
            variant="contained"
            startIcon={<FaPlug />}
            onClick={connect}
            sx={{ ...railBtn(true), justifyContent: "center", fontSize: 11, py: 0.15 }}
          >
            {status === "connecting" ? "Connecting…" : "Connect"}
          </Button>
        )}
        <Button
          size="small"
          fullWidth
          variant="outlined"
          startIcon={<FaExpand />}
          onClick={fullscreen}
          disabled={status !== "connected"}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
        >
          Fullscreen
        </Button>
        {accounts.length > 0 && (
          <>
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, opacity: 0.85, pt: 0.5 }}>
              ACCOUNTS
            </Typography>
          {accounts.map((a) => (
            <Stack key={a.profile} direction="row" alignItems="center" spacing={0.25}>
              <Checkbox
                size="small"
                sx={tick}
                checked={picked.includes(a.profile)}
                disabled={!a.running}
                onChange={() => toggle(picked, setPicked, a.profile)}
                title={`Include ${a.name} in the run`}
              />
              <Button
                size="small"
                fullWidth
                variant={active === a.profile ? "contained" : "outlined"}
                disabled={!a.running || switching === a.profile}
                onClick={() => switchTo(a.profile)}
                sx={{ ...railBtn(active === a.profile), fontSize: 11, minWidth: 0, py: 0.15 }}
                title={
                  a.running
                    ? `Show ${a.name} on screen (port ${a.port})`
                    : `${a.name} browser is not running on the server`
                }
              >
                {switching === a.profile ? "…" : a.name.replace(/^FK /, "")}
                {!a.running && " ⚠"}
              </Button>
            </Stack>
          ))}
          <Stack direction="row" spacing={0.5} sx={{ pt: 0.25 }}>
            <Button
              size="small"
              fullWidth
              variant="outlined"
              onClick={() => setPicked(accounts.filter((a) => a.running).map((a) => a.profile))}
              sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
            >
              All
            </Button>
            <Button
              size="small"
              fullWidth
              variant="outlined"
              onClick={() => setPicked([])}
              sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
            >
              None
            </Button>
          </Stack>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
            <Button
              size="small"
              fullWidth
              variant="contained"
              color="success"
              startIcon={<FaUserCheck />}
              disabled={!picked.length || running || serverBusy}
              onClick={autoLoginSelected}
              sx={{ justifyContent: "center", fontSize: 10.5, py: 0.25, fontWeight: 800 }}
              title={
                picked.length
                  ? `Auto-login/check ${picked.length} selected Flipkart account(s), read-only`
                  : "Tick at least one account for Auto Login"
              }
            >
              Auto Login
            </Button>
            <Button
              size="small"
              fullWidth
              variant="contained"
              color="error"
              startIcon={<FaPowerOff />}
              disabled={!picked.length || running || serverBusy}
              onClick={logoutSelected}
              sx={{ justifyContent: "center", fontSize: 10.5, py: 0.25, fontWeight: 800 }}
              title={
                picked.length
                  ? `Log out ${picked.length} selected Flipkart account(s) once in parallel`
                  : "Tick at least one account to Logout"
              }
            >
              Logout
            </Button>
          </Stack>
          </>
        )}
      </Stack>

      {/* CENTRE — the remote browser. Full-width on phones, middle pane on desktop. */}
      <Box
        sx={{
          order: { xs: 3, md: 0 },
          flexGrow: 1,
          flexBasis: { xs: "100%", md: "auto" },
          width: { xs: "100%", md: "auto" },
          minWidth: { xs: "100%", md: 0 },
          height: { xs: "52vh", md: "auto" },
          display: "flex",
        }}
      >
        <Box
          ref={screenRef}
          tabIndex={0}
          onPointerDown={async () => {
            remoteFocusedRef.current = true;
            try { rfbRef.current?.focus?.(); } catch { /* ignore */ }
            try {
              const text = await navigator.clipboard.readText();
              if (text && rfbRef.current) rfbRef.current.clipboardPasteFrom(text);
            } catch {
              /* Native paste remains available when clipboard permission is denied. */
            }
          }}
          sx={{
            width: "100%",
            height: "100%",
            minHeight: { xs: 340, md: 0 },
            bgcolor: "#0b0b0b",
            borderRadius: 1,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <RemoteMobileKeyboard
            rfbRef={rfbRef}
            onRemoteFocus={() => { remoteFocusedRef.current = true; }}
          />
          {consoleMode === "mac" ? (
            <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ height: "100%", color: "#fff", p: 3, textAlign: "center" }}>
              <FaDesktop size={28} />
              <Typography variant="h6">Mac Chrome Console</Typography>
              <Typography variant="body2" sx={{ maxWidth: 420, opacity: 0.8 }}>
                Flipkart is running in real Chrome windows on this Mac. Select an account on the left to show its profile.
              </Typography>
            </Stack>
          ) : null}
        </Box>
      </Box>

      {/* RIGHT RAIL — operations */}
      <Stack spacing={0.4} sx={{
        order: { xs: 2, md: 0 },
        width: { xs: "calc(50% - 4px)", md: 128 },
        flexShrink: 0,
        color: INK,
        maxHeight: { xs: "34vh", md: "none" },
        overflowY: "auto",
      }}>
        {latestAlert ? (
          <Box sx={{ bgcolor: "#b71c1c", color: "#fff", p: 0.7, borderRadius: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
              ATTENTION: {latestAlert.text}
            </Typography>
            <Button size="small" fullWidth onClick={stopCurrentAlert}
              sx={{ color: "#fff", fontSize: 10, mt: 0.3 }}>STOP ALERT</Button>
          </Box>
        ) : null}
        {pdf && printReady ? (
          <Button fullWidth variant="contained" color="success" startIcon={<FaPrint />}
            onClick={() => { setLogOpen(true); fetchPdf(false); }}
            sx={{ justifyContent: "center", fontSize: 12, py: 0.7, fontWeight: 900 }}>
            PRINT READY{pdf.pages ? ` (${pdf.pages})` : ""}
          </Button>
        ) : null}
        {printReady && !latestAlert ? (
          <Button size="small" fullWidth variant="outlined" color="warning"
            onClick={stopCurrentAlert} sx={{ fontSize: 10, py: 0.2 }}>
            STOP ALERT
          </Button>
        ) : null}
        {taskCompleteAlert ? (
          <Button size="small" fullWidth variant="contained" color="warning"
            startIcon={<FaStop />}
            onClick={stopCurrentAlert} sx={{ fontSize: 10, py: 0.35, fontWeight: 900 }}>
            STOP SOUND
          </Button>
        ) : null}
        {reconWarning ? (
          <Button size="small" fullWidth variant="contained" color="error"
            startIcon={<FaStop />}
            onClick={stopCurrentAlert} sx={{ fontSize: 10, py: 0.35, fontWeight: 900 }}>
            STOP WARNING
          </Button>
        ) : null}
        {remoteCopied ? (
          <Typography variant="caption" sx={{ color: "#8ff0b3", fontWeight: 700 }}>
            Copied from remote
          </Typography>
        ) : null}
        {remoteCopyBlocked && remoteClip ? (
          <Button size="small" fullWidth variant="contained" startIcon={<FaClipboard />}
            onClick={() => copyRemoteToLocal(remoteClip)}
            sx={{ justifyContent: "center", fontSize: 11, py: 0.2 }}>
            Copy remote text
          </Button>
        ) : null}
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: 0.4, opacity: 0.85, pt: 0.5 }}
        >
          FLOW
        </Typography>
        <Stack direction="row" spacing={0.4}>
          <Button
            size="small"
            fullWidth
            variant={flowScope === "today" ? "contained" : "outlined"}
            onClick={() => setFlowScope("today")}
            sx={{ ...railBtn(flowScope === "today"), justifyContent: "center", fontSize: 11, py: 0.15 }}
          >
            Today
          </Button>
          <Button
            size="small"
            fullWidth
            variant={flowScope === "all" ? "contained" : "outlined"}
            onClick={() => setFlowScope("all")}
            sx={{ ...railBtn(flowScope === "all"), justifyContent: "center", fontSize: 11, py: 0.15 }}
          >
            All
          </Button>
        </Stack>
        <Button
          size="small"
          fullWidth
          variant={flowSelected ? "contained" : "outlined"}
          onClick={toggleFlow}
          sx={{ ...railBtn(flowSelected), justifyContent: "center", fontSize: 11, py: 0.15 }}
          title="Select or deselect Accept, Print, and RTD"
        >
          Flow
        </Button>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: 0.4, opacity: 0.85, pt: 0.5 }}
        >
          OPERATIONS
        </Typography>
        {OPS.map((o) => (
          <FormControlLabel
            key={o.key}
            sx={tickLabel}
            control={
              <Checkbox
                size="small"
                sx={tick}
                checked={ops.includes(o.key)}
                onChange={() => toggle(ops, setOps, o.key)}
              />
            }
            label={`${o.icon} ${o.label}`}
          />
        ))}

        {/* While a batch is in flight this becomes STOP, so the run can be
            halted from the same place it was started (with a confirmation —
            stopping mid-way can leave a step half-done). */}
        {(running || serverBusy) ? (
          <Button
            size="small"
            fullWidth
            variant="contained"
            color="error"
            startIcon={<FaStop />}
            disabled={stopping}
            onClick={stopRun}
            sx={{ justifyContent: "center", fontSize: 11, mt: 0.5,
                  fontWeight: 800, bgcolor: "#c62828",
                  "&:hover": { bgcolor: "#a11b1b" } }}
            title="Stop the running batch on the server"
          >
            {stopping ? "Stopping…" : "STOP RUN"}
          </Button>
        ) : (
          <Button
            size="small"
            fullWidth
            variant="contained"
            startIcon={<FaPlay />}
            disabled={!picked.length || !ops.length}
            onClick={runSelected}
            sx={{ ...railBtn(true), justifyContent: "center", fontSize: 11, mt: 0.5 }}
            title={
              picked.length && ops.length
                ? `Run ${ops.length} op(s) on ${picked.length} account(s), in parallel`
                : "Tick at least one account and one operation"
            }
          >
            Run Selected
          </Button>
        )}
        {runLog && !logOpen ? (
          <Button
            size="small"
            fullWidth
            variant="outlined"
            onClick={() => setLogOpen(true)}
            sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
          >
            Show log
          </Button>
        ) : null}
        <Button
          size="small"
          fullWidth
          variant="contained"
          color={schedule.enabled && schedule.status === "online" ? "success" : "error"}
          startIcon={<FaPowerOff />}
          disabled={scheduleBusy}
          onClick={toggleSchedule}
          sx={{ justifyContent: "center", fontSize: 11, py: 0.35 }}
        >
          {scheduleBusy ? "Changing..." : (
            schedule.enabled && schedule.status === "online"
              ? "Schedule ON"
              : schedule.enabled
                ? "START SCHEDULE"
                : "Schedule OFF"
          )}
        </Button>
        <Button
          size="small"
          fullWidth
          variant="outlined"
          onClick={openScheduleSummary}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
        >
          Schedule Summary
        </Button>
        <Button
          size="small"
          fullWidth
          variant="outlined"
          onClick={openHistory}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
        >
          📚 Files
        </Button>
        <Button
          size="small"
          fullWidth
          variant="outlined"
          onClick={() => setDlOpen(true)}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
          title="Everything these browsers downloaded (bulk accept xlsx, CSVs, raw label PDFs)"
        >
          📥 Downloads
        </Button>
        <Button
          size="small"
          fullWidth
          variant="outlined"
          disabled={skuBusy}
          onClick={downloadFlipkartSkuList}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
          title="Download fresh To Accept / To Pack / Upcoming CSVs for selected accounts and build a Flipkart SKU workbook"
        >
          {skuBusy ? "SKU List…" : "📊 Flipkart SKU List"}
        </Button>
        <Button
          size="small"
          fullWidth
          variant="outlined"
          disabled={supportQueueing}
          onClick={openSupportTickets}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
          title="Preview multi-quantity/suspense orders and raise one Flipkart assistance ticket per order"
        >
          {supportQueueing ? "Tickets…" : "🎫 Support Tickets"}
        </Button>
        <Button
          size="small"
          fullWidth
          variant="outlined"
          disabled={dneBusy || dneQueueing}
          onClick={openDneReturns}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
          title="Fetch and track Flipkart returns marked Do not expect physical delivery"
        >
          {dneBusy || dneQueueing ? "DNE…" : "🚫 DNE Returns"}
        </Button>
        <Button
          size="small"
          fullWidth
          variant="outlined"
          disabled={claim60120Busy || claim60120Queueing}
          onClick={open60120Claims}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
          title="Find open Flipkart returns approved 60 to 120 days ago and raise not-received claims"
        >
          {claim60120Busy || claim60120Queueing ? "61-120…" : "📅 61-120 Claim"}
        </Button>
        <Button
          size="small"
          fullWidth
          variant="outlined"
          disabled={multiDimBusy || multiDimSaving}
          onClick={openMultiDimensions}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
          title="Find multi-quantity orders, save dimensions, accept verified groups, and queue support tickets"
        >
          {multiDimBusy || multiDimSaving ? "Multi Dims…" : "📦 Multi Dims"}
        </Button>
        <Button
          size="small"
          fullWidth
          variant="outlined"
          onClick={runReconcile}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
        >
          🔍 Reconcile
        </Button>
        {message ? (
          <Typography variant="caption" sx={{ lineHeight: 1.25, color: "#ffcdd2" }}>
            {message}
          </Typography>
        ) : null}
      </Stack>

      <Dialog open={supportOpen} onClose={() => setSupportOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: 18 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} justifyContent="space-between">
            <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 700 }}>
              Flipkart Support Tickets
            </Typography>
            <TextField
              size="small"
              type="date"
              label="Order date"
              value={supportDate}
              onChange={(e) => {
                setSupportDate(e.target.value);
                setSupportCandidates([]);
                setSupportSelectedIds(new Set());
                setSupportPreviewOpen(false);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 180 }}
            />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
              <Button variant="contained" disabled={supportBusy} onClick={previewSupportTickets}>
                {supportBusy ? "Checking…" : "Preview Multi-Qty"}
              </Button>
              <Button variant="outlined" onClick={() => setSupportManualOpen((prev) => !prev)}>
                {supportManualOpen ? "Hide Manual IDs" : "Manual Order IDs"}
              </Button>
              <Button
                variant="contained"
                color="warning"
                disabled={supportQueueing || !supportSelectedIds.size}
                onClick={queueSupportTickets}
              >
                Raise Selected ({supportSelectedIds.size})
              </Button>
              <Button variant="outlined" onClick={loadSupportTickets}>Refresh Tracker</Button>
              <Button
                variant="outlined"
                color="success"
                disabled={supportTracking || !supportTrackerSelectedRows.length}
                onClick={trackSupportTicketReverts}
              >
                {supportTracking ? "Tracking…" : `Track Reverts (${supportTrackerSelectedRows.length})`}
              </Button>
            </Stack>
            {supportManualOpen ? (
              <Stack spacing={1}>
                <TextField
                  label="Manual order IDs"
                  size="small"
                  multiline
                  minRows={2}
                  value={supportOrderIds}
                  onChange={(e) => setSupportOrderIds(e.target.value)}
                  placeholder="Paste order IDs here, one per line or comma separated"
                />
                <TextField
                  label="Description override (optional)"
                  size="small"
                  multiline
                  minRows={2}
                  value={supportDescription}
                  onChange={(e) => setSupportDescription(e.target.value)}
                  placeholder="Blank uses the standard multi-quantity suspicious-order description"
                  inputProps={{ maxLength: 1990 }}
                />
              </Stack>
            ) : null}

            {supportPreviewOpen ? (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Preview ({supportCandidates.length}) · {supportSelectedIds.size} selected
              </Typography>
              <Box sx={{ border: "1px solid #ddd", maxHeight: 260, overflow: "auto" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "0.3fr 1.15fr 1.2fr 0.35fr 1.1fr 1fr 1.15fr 0.8fr", fontWeight: 700, bgcolor: "#f5f5f5", p: 0.75, gap: 1, alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    checked={supportAllVisibleSelected}
                    indeterminate={supportSomeVisibleSelected && !supportAllVisibleSelected}
                    disabled={!supportSelectableIds.length}
                    onChange={(e) => {
                      setSupportSelectedIds((prev) => {
                        const next = new Set(prev);
                        supportSelectableIds.forEach((id) => {
                          if (e.target.checked) next.add(id);
                          else next.delete(id);
                        });
                        return next;
                      });
                    }}
                  />
                  <span>Account</span><span>Order ID</span><span>Qty</span><span>Tracking</span><span>SKU</span><span>Source</span><span>Status</span>
                </Box>
                {supportCandidates.map((row, idx) => (
                  <Box key={`${row.account_name}-${row.order_id}-${idx}`} sx={{ display: "grid", gridTemplateColumns: "0.3fr 1.15fr 1.2fr 0.35fr 1.1fr 1fr 1.15fr 0.8fr", p: 0.75, gap: 1, borderTop: "1px solid #eee", alignItems: "center" }}>
                    <Checkbox
                      size="small"
                      checked={supportSelectedIds.has(supportCandidateKey(row))}
                      disabled={row?.existing?.status === "SUBMITTED"}
                      onChange={(e) => {
                        const id = supportCandidateKey(row);
                        setSupportSelectedIds((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(id);
                          else next.delete(id);
                          return next;
                        });
                      }}
                    />
                    <span>{row.account_name}</span>
                    <span>{row.order_id}</span>
                    <span>{row.quantity || ""}</span>
                    <span>{row.tracking_id || ""}</span>
                    <span>{row.sku || ""}</span>
                    <span>{row.source || ""}</span>
                    <span>{row.existing?.ticket_number || row.existing?.status || "NEW"}</span>
                  </Box>
                ))}
                {!supportCandidates.length ? (
                  <Box sx={{ p: 1, color: "text.secondary" }}>No preview rows yet.</Box>
                ) : null}
              </Box>
            </Box>
            ) : null}

            <Box>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} sx={{ mb: 0.75 }}>
                <Typography variant="subtitle2" sx={{ minWidth: 170 }}>
                  Ticket Tracker ({supportTickets.length}) · {supportTrackerSelectedRows.length} selected
                </Typography>
                <TextField
                  size="small"
                  label="Search"
                  value={supportFilters.q}
                  onChange={(e) => setSupportFilters((prev) => ({ ...prev, q: e.target.value }))}
                  placeholder="order, ticket, tracking, SKU"
                  sx={{ minWidth: 220 }}
                />
                <TextField
                  select
                  size="small"
                  label="Account"
                  value={supportFilters.account}
                  onChange={(e) => setSupportFilters((prev) => ({ ...prev, account: e.target.value }))}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="all">All</MenuItem>
                  {accounts.map((a) => <MenuItem key={a.name} value={a.name}>{a.name.replace(/^FK /, "")}</MenuItem>)}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={supportFilters.status}
                  onChange={(e) => setSupportFilters((prev) => ({ ...prev, status: e.target.value }))}
                  sx={{ minWidth: 135 }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="QUEUED">Queued</MenuItem>
                  <MenuItem value="SUBMITTED">Submitted</MenuItem>
                  <MenuItem value="ERROR">Error</MenuItem>
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Flipkart"
                  value={supportFilters.responseStatus}
                  onChange={(e) => setSupportFilters((prev) => ({ ...prev, responseStatus: e.target.value }))}
                  sx={{ minWidth: 135 }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="blank">Blank</MenuItem>
                  <MenuItem value="Open">Open</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Resolved">Resolved</MenuItem>
                  <MenuItem value="Closed">Closed</MenuItem>
                </TextField>
                <Button size="small" variant="outlined" onClick={loadSupportTickets}>Apply</Button>
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 0.75 }}>
                <TextField
                  size="small"
                  type="date"
                  label="From"
                  value={supportFilters.from}
                  onChange={(e) => setSupportFilters((prev) => ({ ...prev, from: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 150 }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="To"
                  value={supportFilters.to}
                  onChange={(e) => setSupportFilters((prev) => ({ ...prev, to: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 150 }}
                />
                <TextField
                  select
                  size="small"
                  label="Issue"
                  value={supportFilters.issueType}
                  onChange={(e) => setSupportFilters((prev) => ({ ...prev, issueType: e.target.value }))}
                  sx={{ minWidth: 165 }}
                >
                  <MenuItem value="all">All Issues</MenuItem>
                  <MenuItem value="multi_quantity">Multi Quantity</MenuItem>
                  <MenuItem value="do_not_expect_physical_delivery">DNE / SPF</MenuItem>
                  <MenuItem value="return_not_received_60_120">61–120 Claim</MenuItem>
                  <MenuItem value="manual_suspense">Manual Suspense</MenuItem>
                </TextField>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSupportFilters({ q: "", account: "all", status: "all", responseStatus: "all", issueType: "all", from: "", to: "" })}
                >
                  Clear Filters
                </Button>
              </Stack>
              <Box sx={{ border: "1px solid #ddd", maxHeight: 380, overflow: "auto" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "44px 44px 130px 205px 54px 205px 120px 110px 100px 150px 150px", minWidth: 1360, fontWeight: 700, bgcolor: "#f5f5f5", p: 0.75, gap: 1, position: "sticky", top: 0, zIndex: 1, alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    checked={supportTrackerAllVisibleSelected}
                    indeterminate={supportTrackerSomeVisibleSelected && !supportTrackerAllVisibleSelected}
                    disabled={!supportTrackerTrackableIds.length}
                    onChange={(e) => {
                      setSupportTrackerSelectedIds((prev) => {
                        const next = new Set(prev);
                        supportTrackerTrackableIds.forEach((id) => {
                          if (e.target.checked) next.add(id);
                          else next.delete(id);
                        });
                        return next;
                      });
                    }}
                  />
                  <span></span><span>Account</span><span>Order ID</span><span>Qty</span><span>Ticket</span><span>Status</span><span>Flipkart</span><span>Action</span><span>Created At</span><span>Updated</span>
                </Box>
                {supportTickets.map((row) => (
                  <Box key={row.id} sx={{ borderTop: "1px solid #eee" }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "44px 44px 130px 205px 54px 205px 120px 110px 100px 150px 150px", minWidth: 1360, p: 0.75, gap: 1, alignItems: "center" }}>
                      <Checkbox
                        size="small"
                        checked={supportTrackerSelectedIds.has(row.id)}
                        disabled={row.status !== "SUBMITTED" || !row.ticket_number}
                        onChange={(e) => {
                          setSupportTrackerSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(row.id);
                            else next.delete(row.id);
                            return next;
                          });
                        }}
                      />
                      <Button size="small" variant="text" onClick={() => setSupportExpanded((prev) => (prev === row.id ? "" : row.id))}>
                        {supportExpanded === row.id ? "-" : "+"}
                      </Button>
                      <span>{row.account_name}</span>
                      <span>{row.order_id}</span>
                      <span>{row.quantity || ""}</span>
                      <span>{row.ticket_number || ""}</span>
                      <Chip size="small" label={row.status || "NEW"} color={row.status === "SUBMITTED" ? "success" : row.status === "ERROR" ? "error" : "warning"} />
                      <span>{row.response_status || ""}</span>
                      <Chip size="small" label={row.action_summary || "WAIT"} color={supportActionColor(row.action_summary)} />
                      <span>{formatDatabaseDateTime(row.created_at)}</span>
                      <span>{formatDatabaseDateTime(row.updated_at)}</span>
                    </Box>
                    {supportExpanded === row.id ? (
                      <Box sx={{ p: 1, bgcolor: "#fafafa", borderTop: "1px solid #eee" }}>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                          <Typography variant="body2"><b>Tracking:</b> {row.tracking_id || ""}</Typography>
                          <Typography variant="body2"><b>SKU:</b> {row.sku || ""}</Typography>
                          <Typography variant="body2"><b>Issue:</b> {row.issue_type || ""}</Typography>
                          <Typography variant="body2"><b>Source:</b> {row.source || ""}</Typography>
                          <Typography variant="body2"><b>Last checked:</b> {formatDatabaseDateTime(row.last_checked_at)}</Typography>
                          <Typography variant="body2"><b>Method:</b> {row.automation_method || ""}</Typography>
                          <Typography variant="body2"><b>Action:</b> {row.action_summary || "WAIT"}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}><b>Description sent:</b> {row.description || ""}</Typography>
                        <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}><b>Flipkart revert/status text:</b> {row.last_response || ""}</Typography>
                        {row.error ? <Typography variant="body2" color="error" sx={{ mt: 1, whiteSpace: "pre-wrap" }}><b>Error:</b> {row.error}</Typography> : null}
                      </Box>
                    ) : null}
                  </Box>
                ))}
                {!supportTickets.length ? (
                  <Box sx={{ p: 1, color: "text.secondary" }}>No tickets saved yet.</Box>
                ) : null}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      <ConsoleDialog open={multiDimOpen} onClose={() => setMultiDimOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ py: 1.2, fontSize: 18 }}>
          Flipkart Multi Dims
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
          <Stack spacing={1.25} sx={{ minHeight: 0 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
              <Button variant="contained" disabled={multiDimBusy} onClick={previewMultiDimensions}>
                {multiDimBusy ? "Checking…" : "Find Missing Multi"}
              </Button>
              <Button
                variant="contained"
                color="warning"
                disabled={multiDimSaving}
                onClick={() => saveMultiDimensions()}
              >
                {multiDimSaving ? "Processing…" : "Save + Accept + Tickets"}
              </Button>
              <Chip size="small" color="success" label={`${multiDimSafeCount} safe`} />
              <Chip size="small" color={multiDimInterventionCount ? "error" : "default"} label={`${multiDimInterventionCount} intervention`} />
            </Stack>
            <Box sx={{ border: "1px solid #ddd", maxHeight: "min(430px, 65vh)", minHeight: 160, overflow: "auto" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 0.32fr 1.35fr 0.95fr 1.05fr 0.75fr 0.6fr 1.25fr", fontWeight: 700, bgcolor: "#f5f5f5", p: 0.75, gap: 1, position: "sticky", top: 0, zIndex: 1 }}>
                <span>Account</span><span>Qty</span><span>SKU</span><span>FSN</span><span>Dims</span><span>Wt</span><span>Vol</span><span>Status</span>
              </Box>
              {multiDimItems.map((row, idx) => (
                <Box key={`${row.account}-${row.sku}-${row.fsn}-${idx}`} sx={{ display: "grid", gridTemplateColumns: "1fr 0.32fr 1.35fr 0.95fr 1.05fr 0.75fr 0.6fr 1.25fr", p: 0.75, gap: 1, borderTop: "1px solid #eee" }}>
                  <span>{row.account}</span>
                  <span>{row.qty || ""}</span>
                  <span>{row.sku || ""}</span>
                  <span>{row.fsn || ""}</span>
                  <span>{row.auto_dimensions?.length ? `${row.auto_dimensions.length}x${row.auto_dimensions.breadth}x${row.auto_dimensions.height}` : ""}</span>
                  <span>{row.auto_dimensions?.weight || ""}</span>
                  <span>{row.auto_dimensions?.volumetric_weight || ""}</span>
                  <span>
                    {row.progress_stage
                      ? `${MULTI_DIM_STAGE_LABELS[row.progress_stage] || row.progress_stage}${row.progress_message ? ` · ${row.progress_message}` : ""}`
                      : row.needs_intervention
                      ? (row.error || row.intervention_reason || "Needs intervention")
                      : row.accepted === true
                        ? "Accepted"
                        : row.ok === true
                          ? `Saved · ${row.accept_error || "accept not confirmed"}`
                        : row.ok === false
                          ? (row.error || "Error")
                          : (row.auto_dimensions?.rule || "Ready")}
                    {row.needs_weight_confirmation ? (
                      <Button
                        size="small"
                        color="warning"
                        variant="outlined"
                        disabled={multiDimSaving}
                        onClick={() => saveMultiDimensions(row)}
                        sx={{ mt: 0.5, whiteSpace: "nowrap" }}
                      >
                        Retry {row.suggested_weight_kg} kg
                      </Button>
                    ) : null}
                    {row.verification ? (
                      <Typography component="div" variant="caption" sx={{ mt: 0.5 }}>
                        Verify: {row.verification.weight_verified ? "weight" : "weight pending"} · {row.verification.accepted_confirmed ? "accepted" : "accept pending"} · {row.verification.order_id_found ? "order ID" : "order ID pending"} · {row.verification.ticket_confirmed ? `ticket ${row.verification.ticket_number || "confirmed"}` : "ticket pending"}
                      </Typography>
                    ) : null}
                  </span>
                </Box>
              ))}
              {!multiDimItems.length ? (
                <Box sx={{ p: 1, color: "text.secondary" }}>No missing multi-dimension rows loaded.</Box>
              ) : null}
            </Box>
            {multiDimResult ? (
              <Typography variant="body2" color={multiDimResult.ok ? "success.main" : "error"}>
                {multiDimResult.action === "save"
                  ? `${multiDimResult.saved || 0} saved · ${multiDimResult.accepted || 0} accepted · ${multiDimResult.ticket_queue?.queued || 0} tickets queued`
                  : `${multiDimResult.total || 0} missing multi order(s) found`}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
      </ConsoleDialog>

      <Dialog open={claim60120Open} onClose={() => setClaim60120Open(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ py: 1.2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} justifyContent="space-between">
            <Typography component="span" sx={{ fontSize: 18, fontWeight: 600 }}>Flipkart 61-120 Claim</Typography>
            <Stack direction="row" spacing={1}>
              <Chip size="small" color="success" variant="outlined" label={`${claim60120Summary.approved_count || 0} claims approved`} />
              <Chip size="small" color="success" label={`Total approved: ₹${formatClaimAmount(claim60120Summary.approved_amount)}`} />
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Typography variant="body2" color="text.secondary">
              Selects returns aged 61 through 120 completed days, applies Return State: In Transit, and fetches every Order ID and Order Item ID through Flipkart's authenticated API.
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
              <Button variant="contained" disabled={claim60120Busy} onClick={preview60120Claims}>
                {claim60120Busy ? "Fetching…" : "Fetch Selected Accounts"}
              </Button>
              <Button variant="contained" color="warning" disabled={claim60120Queueing || !claim60120SelectedRaiseIds.length} onClick={queue60120Tickets}>
                Submit Selected ({claim60120SelectedRaiseIds.length})
              </Button>
              <Button variant="contained" color="warning" disabled={claim60120Queueing || !claim60120RaiseableIds.length} onClick={queueAllVisible60120Tickets}>
                Submit All Visible ({claim60120RaiseableIds.length})
              </Button>
              <Button variant="outlined" color="success" disabled={supportTracking || !claim60120SelectedTrackIds.length} onClick={track60120TicketStatus}>
                {supportTracking ? "Tracking…" : `Track Status (${claim60120SelectedTrackIds.length})`}
              </Button>
              <Button variant="outlined" onClick={load60120Claims}>Refresh</Button>
              <Button variant="outlined" onClick={() => setClaim60120SelectedIds(new Set(claim60120SelectableIds))}>Select Visible</Button>
              <Button variant="outlined" onClick={() => setClaim60120SelectedIds(new Set())}>None</Button>
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
              <TextField
                size="small" label="Search" value={claim60120Filters.q}
                onChange={(e) => setClaim60120Filters((prev) => ({ ...prev, q: e.target.value }))}
                placeholder="order, return, ticket, tracking, SKU" sx={{ minWidth: 230 }}
              />
              <TextField
                select size="small" label="Account" value={claim60120Filters.account}
                onChange={(e) => setClaim60120Filters((prev) => ({ ...prev, account: e.target.value }))}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="all">All</MenuItem>
                {accounts.map((a) => <MenuItem key={a.name} value={a.name}>{a.name.replace(/^FK /, "")}</MenuItem>)}
              </TextField>
              <TextField
                select size="small" label="Ticket" value={claim60120Filters.ticketStatus}
                onChange={(e) => setClaim60120Filters((prev) => ({ ...prev, ticketStatus: e.target.value }))}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="all">All</MenuItem><MenuItem value="blank">No Ticket</MenuItem>
                <MenuItem value="QUEUED">Queued</MenuItem><MenuItem value="SUBMITTED">Submitted</MenuItem>
                <MenuItem value="SUBMITTED_NO_TICKET">Submitted, no ticket</MenuItem>
                <MenuItem value="READY_MANUAL">Ready for manual submit</MenuItem>
                <MenuItem value="ERROR">Error</MenuItem>
              </TextField>
              <Button size="small" variant="outlined" onClick={load60120Claims}>Apply</Button>
              <Button size="small" variant="outlined" onClick={() => setClaim60120Filters({ q: "", account: "all", ticketStatus: "all" })}>Clear</Button>
            </Stack>
            <Box sx={{ border: "1px solid #ddd", maxHeight: 520, overflow: "auto" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "0.35fr 1fr 1.65fr 0.55fr 1.45fr 1.45fr 1.1fr 1fr", minWidth: 1240, fontWeight: 700, bgcolor: "#f5f5f5", p: 0.75, gap: 1, position: "sticky", top: 0, zIndex: 1 }}>
                <Checkbox size="small" checked={claim60120AllVisibleSelected} indeterminate={claim60120SomeVisibleSelected && !claim60120AllVisibleSelected} disabled={!claim60120SelectableIds.length} onChange={toggle60120AllVisible} sx={{ p: 0 }} />
                <span>Account</span><span>Order ID</span><span>Age</span><span>Incident / Ticket ID</span><span>Status</span><span>Ticket Created At</span><span>Last Seen</span>
              </Box>
              {claim60120Rows.map((row) => (
                <Box key={row.id} sx={{ borderTop: "1px solid #eee" }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "0.35fr 1fr 1.65fr 0.55fr 1.45fr 1.45fr 1.1fr 1fr", minWidth: 1240, p: 0.75, gap: 1, alignItems: "center" }}>
                    <Checkbox size="small" checked={claim60120SelectedIds.has(row.id)} disabled={Number(row.age_days) < 61 || Number(row.age_days) > 120 || DNE_BLOCKED_SELECT_STATUSES.includes(row.support_ticket_status)} onChange={() => toggle60120Selected(row.id)} />
                    <span>{row.account_name}</span>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                      <Button size="small" variant="text" onClick={() => setClaim60120Expanded((prev) => (prev === row.id ? "" : row.id))} sx={{ minWidth: 28, px: 0.5 }}>{claim60120Expanded === row.id ? "-" : "+"}</Button>
                      <Typography variant="body2" noWrap title={row.order_id || ""}>{row.order_id || ""}</Typography>
                    </Stack>
                    <span>{row.age_days !== null && row.age_days !== undefined && row.age_days !== "" && Number.isFinite(Number(row.age_days)) ? `${row.age_days}d` : "—"}</span>
                    <Typography variant="body2" noWrap title={dneTicketLabel(row)} sx={{ fontFamily: "monospace" }}>{dneTicketLabel(row) || "—"}</Typography>
                    <Chip size="small" color={claimStatusColor(row)} variant={claimStatusColor(row) === "default" ? "outlined" : "filled"} label={dneStatusLabel(row) || "Not submitted"} sx={{ justifySelf: "start", maxWidth: "100%" }} />
                    <span>{formatDatabaseDateTime(row.ticket_created_at)}</span>
                    <span>{formatDatabaseDateTime(row.last_seen_at)}</span>
                  </Box>
                  {claim60120Expanded === row.id ? (
                    <Box sx={{ p: 1, bgcolor: "#fafafa", borderTop: "1px solid #eee" }}>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1 }}>
                        <Typography variant="body2"><b>Order Item ID:</b> {row.order_item_id || ""}</Typography>
                        <Typography variant="body2"><b>Return ID:</b> {row.return_id || ""}</Typography>
                        <Typography variant="body2"><b>Qty:</b> {row.quantity || ""}</Typography>
                        <Typography variant="body2"><b>Tracking:</b> {row.tracking_id || ""}</Typography>
                        <Typography variant="body2"><b>SKU:</b> {row.sku || ""}</Typography>
                        <Typography variant="body2"><b>Approved:</b> {row.return_approved_on || ""}</Typography>
                        <Typography variant="body2"><b>Status:</b> {dneStatusLabel(row)}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}><b>Description:</b> {row.description || ""}</Typography>
                      <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}><b>Flipkart reply:</b> {row.last_response || ""}</Typography>
                      {row.support_ticket_error ? <Typography variant="body2" color="error" sx={{ mt: 1 }}><b>Error:</b> {row.support_ticket_error}</Typography> : null}
                    </Box>
                  ) : null}
                </Box>
              ))}
              {!claim60120Rows.length ? <Box sx={{ p: 1, color: "text.secondary" }}>No 61-120 return claims saved yet.</Box> : null}
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={dneOpen} onClose={() => setDneOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ py: 1.2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} justifyContent="space-between">
            <Typography component="span" sx={{ fontSize: 18, fontWeight: 600 }}>Flipkart DNE Returns</Typography>
            <Stack direction="row" spacing={1}>
              <Chip size="small" color="success" variant="outlined" label={`${dneSummary.approved_count || 0} claims approved`} />
              <Chip size="small" color="success" label={`Total approved: ₹${formatClaimAmount(dneSummary.approved_amount)}`} />
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Typography variant="body2" color="text.secondary">
              Finds returns in Return State: Do not expect physical delivery for last 90 days, saves unique order IDs, then raises DNE/SPF claims for selected rows.
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
              <Button variant="contained" disabled={dneBusy} onClick={previewDneReturns}>
                {dneBusy ? "Fetching…" : "Fetch All Accounts"}
              </Button>
              <Button variant="contained" color="warning" disabled={dneQueueing || !dneSelectedRaiseIds.length} onClick={queueDneTickets}>
                Submit Selected ({dneSelectedRaiseIds.length})
              </Button>
              <Button variant="contained" color="error" disabled={dneQueueing || !dneSelectedReraiseIds.length} onClick={reraiseDneTickets}>
                Reraise Selected ({dneSelectedReraiseIds.length})
              </Button>
              <Button variant="contained" color="warning" disabled={dneQueueing || !dneRaiseableIds.length} onClick={queueAllVisibleDneTickets}>
                Submit All Visible ({dneRaiseableIds.length})
              </Button>
              <Button
                variant="outlined"
                color="success"
                disabled={supportTracking || !dneSelectedSubmittedCount}
                onClick={trackDneTicketStatus}
                title={dneSelectedSubmittedWithoutTicketCount ? `${dneSelectedSubmittedWithoutTicketCount} selected submitted DNE/SPF claim(s) have no Flipkart ticket number yet.` : ""}
              >
                {supportTracking ? "Tracking…" : `Track Status (${dneSelectedSubmittedCount})`}
              </Button>
              <Button variant="outlined" onClick={loadDneReturns}>Refresh</Button>
              <Button
                variant="outlined"
                onClick={() => setDneSelectedIds(new Set(dneSelectableIds))}
              >
                Select Visible
              </Button>
              <Button variant="outlined" onClick={() => setDneSelectedIds(new Set())}>None</Button>
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
              <TextField
                size="small"
                label="Search"
                value={dneFilters.q}
                onChange={(e) => setDneFilters((prev) => ({ ...prev, q: e.target.value }))}
                placeholder="order, return, ticket, tracking, SKU"
                sx={{ minWidth: 230 }}
              />
              <TextField
                select
                size="small"
                label="Account"
                value={dneFilters.account}
                onChange={(e) => setDneFilters((prev) => ({ ...prev, account: e.target.value }))}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="all">All</MenuItem>
                {accounts.map((a) => <MenuItem key={a.name} value={a.name}>{a.name.replace(/^FK /, "")}</MenuItem>)}
              </TextField>
              <TextField
                select
                size="small"
                label="DNE status"
                value={dneFilters.status}
                onChange={(e) => setDneFilters((prev) => ({ ...prev, status: e.target.value }))}
                sx={{ minWidth: 135 }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="FOUND">Found</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Ticket"
                value={dneFilters.ticketStatus}
                onChange={(e) => setDneFilters((prev) => ({ ...prev, ticketStatus: e.target.value }))}
                sx={{ minWidth: 135 }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="blank">No Ticket</MenuItem>
                <MenuItem value="QUEUED">Queued</MenuItem>
                <MenuItem value="SUBMITTED">Submitted</MenuItem>
                <MenuItem value="SUBMITTED_NO_TICKET">Submitted, no ticket</MenuItem>
                <MenuItem value="READY_MANUAL">Ready for manual submit</MenuItem>
                <MenuItem value="AUTO_RESOLVED_30_DAYS">Support not needed (completed after 30 days)</MenuItem>
                <MenuItem value="AUTO_RESOLVED">Auto-resolved</MenuItem>
                <MenuItem value="ERROR">Error</MenuItem>
              </TextField>
              <Button size="small" variant="outlined" onClick={loadDneReturns}>Apply</Button>
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
              <TextField
                size="small"
                type="date"
                label="Seen from"
                value={dneFilters.from}
                onChange={(e) => setDneFilters((prev) => ({ ...prev, from: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
              <TextField
                size="small"
                type="date"
                label="Seen to"
                value={dneFilters.to}
                onChange={(e) => setDneFilters((prev) => ({ ...prev, to: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => setDneFilters({ q: "", account: "all", status: "all", ticketStatus: "all", from: "", to: "" })}
              >
                Clear Filters
              </Button>
            </Stack>

            <Box sx={{ border: "1px solid #ddd", maxHeight: 520, overflow: "auto" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "0.35fr 1fr 1.55fr 0.6fr 1.35fr 1.45fr 1.1fr 1fr", minWidth: 1220, fontWeight: 700, bgcolor: "#f5f5f5", p: 0.75, gap: 1, position: "sticky", top: 0, zIndex: 1 }}>
                <Checkbox
                  size="small"
                  checked={dneAllVisibleSelected}
                  indeterminate={dneSomeVisibleSelected && !dneAllVisibleSelected}
                  disabled={!dneSelectableIds.length}
                  onChange={toggleDneAllVisible}
                  sx={{ p: 0 }}
                />
                <span>Account</span><span>Order ID</span><span>Age</span><span>Incident / Ticket ID</span><span>Status</span><span>Ticket Created At</span><span>Last Seen</span>
              </Box>
              {dneRows.map((row) => (
                <Box key={row.id} sx={{ borderTop: "1px solid #eee" }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "0.35fr 1fr 1.55fr 0.6fr 1.35fr 1.45fr 1.1fr 1fr", minWidth: 1220, p: 0.75, gap: 1, alignItems: "center" }}>
                    <Checkbox
                      size="small"
                      checked={dneSelectedIds.has(row.id)}
                      disabled={Number(row.age_days) < 61 || Number(row.age_days) > 120 || DNE_BLOCKED_SELECT_STATUSES.includes(row.support_ticket_status)}
                      onChange={() => toggleDneSelected(row.id)}
                    />
                    <span>{row.account_name}</span>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                      <Button size="small" variant="text" onClick={() => setDneExpanded((prev) => (prev === row.id ? "" : row.id))} sx={{ minWidth: 28, px: 0.5 }}>
                        {dneExpanded === row.id ? "-" : "+"}
                      </Button>
                      <Typography variant="body2" noWrap title={row.order_id || ""}>{row.order_id || ""}</Typography>
                    </Stack>
                    <span>{row.age_days !== null && row.age_days !== undefined && row.age_days !== "" && Number.isFinite(Number(row.age_days)) ? `${row.age_days}d` : "—"}</span>
                    <Typography variant="body2" noWrap title={dneTicketLabel(row)} sx={{ fontFamily: "monospace" }}>{dneTicketLabel(row) || "—"}</Typography>
                    <Chip size="small" color={claimStatusColor(row)} variant={claimStatusColor(row) === "default" ? "outlined" : "filled"} label={dneStatusLabel(row) || "Not submitted"} sx={{ justifySelf: "start", maxWidth: "100%" }} />
                    <span>{formatDatabaseDateTime(row.ticket_created_at)}</span>
                    <span>{formatDatabaseDateTime(row.last_seen_at)}</span>
                  </Box>
                  {dneExpanded === row.id ? (
                    <Box sx={{ p: 1, bgcolor: "#fafafa", borderTop: "1px solid #eee" }}>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1 }}>
                        <Typography variant="body2"><b>Order Item ID:</b> {row.order_item_id || ""}</Typography>
                        <Typography variant="body2"><b>Return ID:</b> {row.return_id || ""}</Typography>
                        <Typography variant="body2"><b>Qty:</b> {row.quantity || ""}</Typography>
                        <Typography variant="body2"><b>Tracking:</b> {row.tracking_id || ""}</Typography>
                        <Typography variant="body2"><b>SKU:</b> {row.sku || ""}</Typography>
                        <Typography variant="body2"><b>Return type:</b> {row.return_type || ""}</Typography>
                        <Typography variant="body2"><b>Action:</b> {row.closed_action || ""}</Typography>
                        <Typography variant="body2"><b>Status:</b> {dneStatusLabel(row)}</Typography>
                        <Typography variant="body2"><b>Flipkart:</b> {row.response_status || ""}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}><b>Description:</b> {row.description || ""}</Typography>
                      <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}><b>Flipkart revert/status text:</b> {row.last_response || ""}</Typography>
                      {row.support_ticket_error ? <Typography variant="body2" color="error" sx={{ mt: 1, whiteSpace: "pre-wrap" }}><b>Error:</b> {row.support_ticket_error}</Typography> : null}
                    </Box>
                  ) : null}
                </Box>
              ))}
              {!dneRows.length ? (
                <Box sx={{ p: 1, color: "text.secondary" }}>No DNE returns saved yet.</Box>
              ) : null}
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Run output. Closing this does NOT stop the run -- it keeps going on the
          server, and "Show log" brings the output back. */}
      <Dialog
        open={logOpen}
        onClose={() => setLogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...(runDialogPos
              ? { position: "fixed", top: runDialogPos.y, left: runDialogPos.x, m: 0 }
              : { position: "relative" }),
            ...(runDialogSize
              ? {
                  width: runDialogSize.width,
                  maxWidth: "calc(100vw - 16px)",
                  height: runDialogSize.height,
                  maxHeight: "calc(100vh - 16px)",
                }
              : {}),
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          onPointerDown={startRunDialogDrag}
          onDoubleClick={() => setRunDialogPos(null)}
          title="Hold and drag to move. Double-click to center."
          sx={{ py: 1, fontSize: 16, cursor: "grab", userSelect: "none", touchAction: "none" }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
            <Box>
            {running ? "Running locally on this Mac…" : "Run finished"}
              <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>
                {picked.length} account(s) in parallel · {(activeRunOps.length ? activeRunOps : ops).join(", ")}
                {running ? " — you can close this; the run continues." : ""}
              </Typography>
            </Box>
            {taskCompleteAlert || attentionAlert || reconWarning ? (
              <Button size="small" variant="contained" color="warning"
                startIcon={<FaStop />} onClick={stopCurrentAlert}
                sx={{ textTransform: "none", fontWeight: 900, flexShrink: 0 }}>
                Stop Sound
              </Button>
            ) : null}
          </Stack>
        </DialogTitle>
        {returnOtc && Array.isArray(returnOtc.results) && returnOtc.results.length ? (
          <Box sx={{ px: 3, py: 2, bgcolor: "#0b3d2e", color: "#fff" }}>
            <Typography variant="caption" sx={{ letterSpacing: 1.5, opacity: 0.85 }}>
              RETURN OTC · {String(returnOtc.mode || "").toUpperCase()}
            </Typography>
            {returnOtc.results.map((r, i) => (
              <Box key={i} sx={{ mt: i ? 1.5 : 0.5 }}>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  {r.account}
                  {r.fes && r.fes[0] && r.fes[0].feName
                    ? " · " + String(r.fes[0].feName).split("_")[0] : ""}
                </Typography>
                {String(returnOtc.mode || "").toLowerCase() === "end" && r.fes?.[0] ? (
                  <Typography variant="caption" sx={{ display: "block", color: r.fes[0].skipConfirmed ? "#9ff0b4" : "#ffd479", fontWeight: 700 }}>
                    {r.fes[0].recoveredFromCapture
                      ? "End OTC recovered from today’s closed task · skip source not attributed"
                      : r.fes[0].skipConfirmed
                        ? "Automatic skip confirmed by Flipkart"
                        : r.fes[0].skipAttempted
                          ? "Automatic skip not confirmed"
                          : "No automatic skip was required"}
                  </Typography>
                ) : null}
                {r.codes ? (
                  <Typography sx={{
                    fontFamily: "monospace", fontWeight: 800,
                    fontSize: { xs: 44, sm: 76 }, lineHeight: 1.1, letterSpacing: 8,
                    userSelect: "all", cursor: "pointer",
                  }}
                    title="Click to copy"
                    onClick={() => { try { navigator.clipboard.writeText(r.codes); } catch { /* */ } }}
                  >
                    {r.codes}
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#ffd479", mt: 0.5 }}>
                    {r.reason || "no code"}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        ) : null}
        {pdf ? (
          <Box sx={{ px: 3, py: 1.5, display: "flex", gap: 1, alignItems: "center",
                     flexWrap: "wrap", bgcolor: "action.hover" }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {isExcelArtifact(pdf) ? "📊 Workbook ready" : "📄 Labels ready"} — {pdf.name}
            </Typography>
            <Button size="small" variant="contained" startIcon={isExcelArtifact(pdf) ? <FaFileExcel /> : <FaPrint />}
                    onClick={() => fetchPdf(false)} sx={{ textTransform: "none" }}>
              {isExcelArtifact(pdf) ? "Open Excel" : "Open & Print"}
            </Button>
            <Button size="small" variant="outlined" startIcon={<FaDownload />}
                    onClick={() => fetchPdf(true)} sx={{ textTransform: "none" }}>
              Download
            </Button>
            {!isExcelArtifact(pdf) && autoPdfUrl ? (
              <Button size="small" variant="outlined"
                      startIcon={pdfPreviewVisible ? <FaEyeSlash /> : <FaEye />}
                      onClick={() => setPdfPreviewVisible((visible) => !visible)}
                      title={pdfPreviewVisible ? "Hide the PDF preview to give the log more space" : "Show the PDF preview"}
                      sx={{ textTransform: "none" }}>
                {pdfPreviewVisible ? "Hide Preview" : "Show Preview"}
              </Button>
            ) : null}
            <Typography variant="caption" sx={{ opacity: 0.7, width: "100%" }}>
              {isExcelArtifact(pdf)
                ? "Excel files download with the correct workbook type so they open in Excel instead of the PDF viewer."
                : "Auto-shown below the moment the server finishes; “Open & Print” pops it full-tab so printing reaches the printer on this machine."}
            </Typography>
          </Box>
        ) : null}
        {autoPdfUrl && pdfPreviewVisible ? (
          <Box sx={{ px: 2, pt: 1 }}>
            <embed src={autoPdfUrl} type="application/pdf"
                   style={{ width: "100%", height: "60vh", border: "1px solid rgba(0,0,0,0.15)",
                            borderRadius: 4 }} />
          </Box>
        ) : null}
        <DialogContent
          ref={runLogBoxRef}
          dividers
          sx={{ flex: 1, minHeight: 0, maxHeight: runDialogSize ? "none" : "70vh", overflowY: "auto" }}
        >
          <Box
            component="pre"
            sx={{
              m: 0, fontSize: 12, lineHeight: 1.45, whiteSpace: "pre-wrap",
              wordBreak: "break-word", minHeight: "100%",
            }}
          >
            {runLog || "…"}
          </Box>
        </DialogContent>
        <Box
          onPointerDown={startRunDialogResize}
          title="Hold and drag corner to resize."
          sx={{
            position: "absolute",
            right: 0,
            bottom: 0,
            width: 24,
            height: 24,
            cursor: "nwse-resize",
            zIndex: 3,
            "&::after": {
              content: '""',
              position: "absolute",
              right: 6,
              bottom: 6,
              width: 10,
              height: 10,
              borderRight: "2px solid rgba(0,0,0,0.45)",
              borderBottom: "2px solid rgba(0,0,0,0.45)",
            },
          }}
        />
      </Dialog>

      <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ py: 0.75, pl: 2, pr: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography component="span" sx={{ fontSize: 18, fontWeight: 600 }}>
              Flipkart Run Summary
            </Typography>
            <Tooltip title="Schedule Settings">
              <IconButton
                aria-label="Open Schedule Settings"
                onClick={() => {
                  setScheduleOpen(false);
                  openScheduleSettings();
                }}
              >
                <FaCog size={18} />
              </IconButton>
            </Tooltip>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={0.75}>
            {scheduleRuns.map((run) => (
              <Box key={run.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                <Box onClick={() => setExpandedRun(expandedRun === run.id ? "" : run.id)}
                  sx={{ display: "grid", gridTemplateColumns: "170px 100px 100px minmax(220px, 1fr) 110px", gap: 1,
                    px: 1.5, py: 1, cursor: "pointer", alignItems: "center" }}>
                  <Typography variant="body2">{formatDatabaseDateTime(run.scheduledAt || run.at)}</Typography>
                  <Chip size="small" label={run.type} color={run.type === "Scheduled" ? "primary" : "default"} />
                  <Typography variant="body2">{run.scope || "Today"}</Typography>
                  <Typography variant="body2">{(run.operations || []).join(", ") || "No operations recorded"}</Typography>
                  <Typography variant="body2"><b>{run.result}</b> {expandedRun === run.id ? "▴" : "▾"}</Typography>
                </Box>
                {expandedRun === run.id ? (
                  <Box sx={{ px: 2, py: 1, bgcolor: "action.hover" }}>
                    <Typography variant="caption" display="block">Scheduled slot: {run.id}</Typography>
                    {run.attemptAt ? <Typography variant="caption" display="block">Last attempt: {formatDatabaseDateTime(run.attemptAt)}</Typography> : null}
                    {run.reason ? <Typography variant="caption" display="block">Reason: {run.reason}</Typography> : null}
                    {run.retryAt ? <Typography variant="caption" display="block">Retry: {new Date(run.retryAt).toLocaleString()}</Typography> : null}
                  </Box>
                ) : null}
              </Box>
            ))}
            {!scheduleRuns.length ? <Typography variant="body2">No completed runs found.</Typography> : null}
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={scheduleSettingsOpen} onClose={() => setScheduleSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ py: 1.2, fontSize: 18 }}>Schedule Settings</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            {editSchedules.map((row, index) => (
              <Box key={`${row.time}-${index}`} sx={{ border: 1, borderColor: "divider", p: 1.25, borderRadius: 1 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField size="small" type="time" value={row.time} sx={{ minWidth: 145 }}
                    onChange={(e) => setEditSchedules((old) => old.map((x, i) => i === index ? { ...x, time: e.target.value } : x))} />
                  <TextField select size="small" label="Orders" value={row.scope} sx={{ minWidth: 145 }}
                    onChange={(e) => setEditSchedules((old) => old.map((x, i) => i === index ? {
                      ...x,
                      scope: e.target.value,
                    } : x))}>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="all">All dates</MenuItem>
                  </TextField>
                  <Button color="error" onClick={() => setEditSchedules((old) => old.filter((_, i) => i !== index))}>Delete</Button>
                </Stack>
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: "wrap" }}>
                  {SCHEDULE_OPTIONS.map(({ key: op, label }) => {
                    return <FormControlLabel key={op} sx={{ mr: 1 }}
                      control={<Checkbox size="small" checked={row.operations.includes(op)}
                        onChange={() => setEditSchedules((old) => old.map((x, i) => i === index ? {
                          ...x,
                          operations: x.operations.includes(op) ? x.operations.filter((v) => v !== op) : [...x.operations, op],
                        } : x))} />}
                      label={label} />;
                  })}
                </Stack>
              </Box>
            ))}
            <Button variant="outlined" onClick={() => setEditSchedules((old) => [...old, {
              time: "08:55", scope: "today", operations: ["accept", "print", "rtd", "upload"],
            }])}>Add Schedule</Button>
            <Button variant="contained" disabled={scheduleBusy || !editSchedules.length} onClick={saveScheduleTimes}>
              Save Schedules
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Return OTC — ask START or END before running. */}
      <Dialog open={otcPickOpen} onClose={() => setOtcPickOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 1.2, fontSize: 18 }}>Return OTC — which code?</DialogTitle>
        <DialogContent sx={{ pb: 2.5 }}>
          <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
            {picked.length} account(s). <b>START</b> = the code you give the delivery
            agent (Wishmaster) when they arrive. <b>END</b> = the code after they finish
            scanning the returns.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button fullWidth variant="contained" color="success"
              sx={{ py: 2, fontSize: 18, fontWeight: 800 }}
              onClick={() => { setOtcPickOpen(false); runSelected("start"); }}>
              START
            </Button>
            <Button fullWidth variant="contained" color="warning"
              sx={{ py: 2, fontSize: 18, fontWeight: 800 }}
              onClick={() => { setOtcPickOpen(false); runSelected("end"); }}>
              END
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: 16 }}>
          📚 Flipkart files
          <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>
            Merged label PDFs and generated SKU-list workbooks — newest first.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" spacing={1} sx={{ mb: 1.25, flexWrap: "wrap" }}>
            <TextField
              size="small" placeholder="Search filename…" value={hSearch}
              onChange={(e) => setHSearch(e.target.value)}
              sx={{ flex: "1 1 160px", minWidth: 140 }}
            />
            <TextField
              size="small" select label="Date" value={hDate}
              onChange={(e) => setHDate(e.target.value)} sx={{ minWidth: 120 }}
            >
              <MenuItem value="">All dates</MenuItem>
              {historyDates.map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small" select label="Sort" value={hSort}
              onChange={(e) => setHSort(e.target.value)} sx={{ minWidth: 120 }}
            >
              <MenuItem value="newest">Newest</MenuItem>
              <MenuItem value="oldest">Oldest</MenuItem>
              <MenuItem value="largest">Largest</MenuItem>
              <MenuItem value="smallest">Smallest</MenuItem>
              <MenuItem value="name">Name A–Z</MenuItem>
            </TextField>
            <Button
              size="small"
              variant={hShowSub ? "contained" : "outlined"}
              onClick={() => setHShowSub((v) => !v)}
              sx={{ textTransform: "none" }}
            >
              {hShowSub ? "Hide Sub Files" : "Show Sub Files"}
            </Button>
          </Stack>
          {historyLoading ? (
            <Typography variant="body2">Loading…</Typography>
          ) : history.length === 0 ? (
            <Typography variant="body2">No Flipkart files yet.</Typography>
          ) : visibleHistory.length === 0 ? (
            <Typography variant="body2">No files match your filters.</Typography>
          ) : (
            <Stack spacing={0.75}>
              <Typography variant="caption" sx={{ opacity: 0.6 }}>
                {visibleHistory.length} of {history.length}
              </Typography>
              {visibleHistory.map((f) => (
                <Box key={f.relpath}
                     sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap",
                           p: 1, borderRadius: 1, bgcolor: "action.hover" }}>
                  <Box sx={{ fontSize: 24, color: f.kind === "sku_list" ? "success.main" : "error.main",
                             display: "flex", alignItems: "center" }}>
                    {f.kind === "sku_list" ? <FaFileExcel /> : <FaFilePdf />}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-all" }}>
                      {f.name}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {f.kind === "sku_list" ? "SKU List" : "Labels PDF"} · {new Date(f.mtime).toLocaleString()} · {(f.size / 1048576).toFixed(2)} MB
                    </Typography>
                  </Box>
                  <Button size="small" variant="contained" startIcon={f.kind === "sku_list" ? <FaFileExcel /> : <FaPrint />}
                          onClick={() => openArchiveFile(f.relpath, f.name, f.kind === "sku_list")}
                          sx={{ textTransform: "none" }}>
                    {f.kind === "sku_list" ? "Open Excel" : "Open"}
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<FaDownload />}
                          onClick={() => openArchiveFile(f.relpath, f.name, true)}
                          sx={{ textTransform: "none" }}>
                    Download
                  </Button>
                  <Tooltip title="Delete file">
                    <IconButton size="small" color="error"
                                aria-label={`Delete ${f.name}`}
                                onClick={() => setHistoryDeleteFile(f)}>
                      <FaTrash />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(historyDeleteFile)}
              onClose={() => { if (!historyDeleting) setHistoryDeleteFile(null); }}
              maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0.75 }}>Delete file?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
            {historyDeleteFile?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            This permanently removes the archived file from the server.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDeleteFile(null)} disabled={historyDeleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" startIcon={<FaTrash />}
                  onClick={deleteHistoryFile} disabled={historyDeleting}>
            {historyDeleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reconOpen} onClose={() => setReconOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: 16 }}>
          🔍 Reconcile — PDF and scan tracking check
          <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>
            Merged PDF AWBs versus scan-upload tracking IDs, plus duplicate labels.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: "wrap" }}>
            <TextField
              size="small"
              type="date"
              label="Dispatch date"
              value={reconDate}
              onChange={(e) => { setReconDate(e.target.value); runReconcile(e.target.value); }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 165 }}
            />
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              = window (D−1 3 PM → D 3 PM). D−1 evening + D morning ship on D.
            </Typography>
          </Stack>
          {reconBusy ? (
            <Typography variant="body2">Scanning all merged PDFs…</Typography>
          ) : !recon ? (
            <Typography variant="body2">—</Typography>
          ) : !recon.ok ? (
            <Typography variant="body2" sx={{ color: "error.main" }}>Error: {String(recon.error)}</Typography>
          ) : (
            <Stack spacing={1}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Dispatch <b>{recon.dispatch}</b> · window {recon.window?.start} → {recon.window?.end} · {recon.file_count} file(s) · {recon.total_pages} labels · {recon.distinct_awbs} distinct AWBs
              </Typography>
              {Number(recon.tracking_match?.scan_only_count || 0) > 0 ? (
                <Box sx={{ p: 1.25, bgcolor: "#ffebee", border: "2px solid", borderColor: "error.main", borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ color: "error.main", fontWeight: 900 }}>
                    ⚠ WARNING — scan tracking IDs missing from PDF
                  </Typography>
                  <Typography variant="body2" sx={{ color: "error.main", fontWeight: 700 }}>
                    {recon.tracking_match.scan_only_count} scan uploaded tracking ID(s) do not appear in merged labels
                    {Number(recon.tracking_match.found_outside_window_count || 0)
                      ? ` · ${recon.tracking_match.found_outside_window_count} already found in older merged PDF`
                      : ""}.
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                    <Button size="small" variant="contained" color="error"
                      onClick={() => setMissingTrackOpen(true)} sx={{ textTransform: "none", fontWeight: 900 }}>
                      Select/Reprint IDs
                    </Button>
                    <Button size="small" variant="outlined" color="error" startIcon={<FaStop />}
                      onClick={stopCurrentAlert} sx={{ textTransform: "none", fontWeight: 900 }}>
                      Stop Warning
                    </Button>
                  </Stack>
                </Box>
              ) : null}
              {recon.clean ? (
                <Typography variant="h6" sx={{ color: "success.main" }}>
                  ✅ No duplicate labels across all {recon.file_count} PDF(s)
                </Typography>
              ) : (
                <>
                  <Typography variant="h6" sx={{ color: "error.main" }}>
                    ⚠ {recon.duplicate_count} DUPLICATE label(s) found
                  </Typography>
                  <Stack spacing={0.5}>
                    {(recon.duplicates || []).map((d, i) => (
                      <Box key={i} sx={{ p: 1, borderRadius: 1, bgcolor: "action.hover" }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>AWB {d.awb}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.75, wordBreak: "break-all" }}>
                          appears on: {(d.locations || []).join("  •  ")}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </>
              )}
              {recon.tracking_match ? (
                <Box sx={{ mt: 1, p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ color: recon.tracking_match.complete && !recon.labels_less_than_scan ? "success.main" : "error.main" }}>
                    {recon.tracking_match.complete && !recon.labels_less_than_scan ? "✅" : "⚠"} Scan match: {recon.tracking_match.matched} matched · {recon.tracking_match.scan_only_count} missing PDF
                    {Number(recon.tracking_match.found_outside_window_count || 0)
                      ? ` · ${recon.tracking_match.found_outside_window_count} ID(s) in ${recon.tracking_match.older_pdf_count || 0} older PDF${Number(recon.tracking_match.older_pdf_count || 0) === 1 ? "" : "s"}`
                      : ""}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Scan uploaded {recon.tracking_match.scan_total} · PDF AWBs {recon.tracking_match.pdf_total} · PDF-only {recon.tracking_match.pdf_only_count}
                  </Typography>
                  {Number(recon.tracking_match.found_outside_window_count || 0) ? (
                    <Box sx={{ mt: 0.75 }}>
                      <Typography variant="caption" sx={{ display: "block", color: "warning.main", fontWeight: 700 }}>
                        {recon.tracking_match.found_outside_window_count} scan ID(s) were found in older merged PDFs outside this dispatch window.
                      </Typography>
                      {(recon.tracking_match.older_pdfs || []).length ? (
                        <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                          {(recon.tracking_match.older_pdfs || []).map((file) => (
                            <Box key={file.relpath}
                              sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap", p: 0.75, bgcolor: "action.hover", borderRadius: 1 }}>
                              <FaFilePdf />
                              <Box sx={{ flex: "1 1 190px", minWidth: 0 }}>
                                <Typography variant="caption" sx={{ display: "block", fontWeight: 700, wordBreak: "break-all" }}>
                                  {file.name}
                                </Typography>
                                <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>
                                  {file.date}{file.time ? ` · ${file.time}` : ""} · {file.tracking_count || 0} matching ID(s)
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                  {(file.tracking_ids || []).map((trackingId, index) => {
                                    const detail = (recon.tracking_match.found_outside_window || [])
                                      .find((row) => String(row?.id || "") === String(trackingId));
                                    const location = (detail?.found_pdfs || [])
                                      .find((loc) => loc?.relpath === file.relpath);
                                    const page = location?.page || (file.pages || [])[index];
                                    return (
                                      <Typography key={trackingId} variant="caption"
                                        sx={{ display: "block", fontFamily: "monospace", wordBreak: "break-word" }}>
                                        {trackingId}
                                        {detail?.account ? ` · ${detail.account}` : ""}
                                        {detail?.order_id ? ` · ${detail.order_id}` : ""}
                                        {detail?.order_item_id ? ` · Item ${detail.order_item_id}` : ""}
                                        {page ? ` · page ${page}` : ""}
                                      </Typography>
                                    );
                                  })}
                                </Box>
                              </Box>
                              <Button size="small" variant="contained" startIcon={<FaEye />}
                                onClick={() => openArchiveFile(file.relpath, file.name, false)}
                                sx={{ textTransform: "none" }}>
                                Open
                              </Button>
                              <Button size="small" variant="outlined" startIcon={<FaDownload />}
                                onClick={() => openArchiveFile(file.relpath, file.name, true)}
                                sx={{ textTransform: "none" }}>
                                Download
                              </Button>
                            </Box>
                          ))}
                        </Stack>
                      ) : null}
                    </Box>
                  ) : null}
                  {recon.tracking_match.scan_only_count ? (
                    <Button size="small" variant="contained" color="error"
                      onClick={() => setMissingTrackOpen(true)}
                      sx={{ mt: 1, textTransform: "none", fontWeight: 800 }}>
                      Select/Reprint Missing Tracking IDs
                    </Button>
                  ) : null}
                  {recon.tracking_match.pdf_only_count ? (
                    <Box component="details" sx={{ mt: 1 }}>
                      <Box component="summary" sx={{ cursor: "pointer", color: "warning.main", fontWeight: 700 }}>
                        PDF-only: possible cancelled/removed orders
                      </Box>
                      <Typography variant="caption" sx={{ display: "block", opacity: 0.75 }}>
                        These are not automatically failures because cancelled orders may no longer appear in scan uploads.
                      </Typography>
                      <Box sx={{ mt: 0.5, maxHeight: 180, overflowY: "auto", fontFamily: "monospace", fontSize: 12 }}>
                        {(recon.tracking_match.pdf_only || []).map((id) => <Box key={id}>{id}</Box>)}
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              ) : null}
              {recon.files?.length ? (
                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                  files: {recon.files.map((f) => `${f.name.replace(/^MERGED_ALL_LABELS_fkt_/, "").replace(/\.pdf$/, "")} (${f.pages}p, ${f.time})`).join(" · ")}
                </Typography>
              ) : null}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={missingTrackOpen} onClose={() => setMissingTrackOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ py: 1.2, fontSize: 18 }}>
          Missing Scan Tracking IDs
          <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>
            Uploaded to scan, but not found in merged Flipkart labels. Reason shows where the row came from.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: "error.main" }}>
                {missingRows.length} missing tracking ID(s) · {selectedMissingRows.length} selected
              </Typography>
              <Button size="small" variant="outlined" color="error"
                onClick={() => setAllMissingSelected(true)}
                disabled={!missingRows.length || selectedMissingRows.length === missingRows.length}
                sx={{ textTransform: "none" }}>
                Select All
              </Button>
              <Button size="small" variant="outlined" color="error"
                onClick={() => setAllMissingSelected(false)}
                disabled={!selectedMissingRows.length}
                sx={{ textTransform: "none" }}>
                Clear
              </Button>
              <Button size="small" variant="contained" color="error" startIcon={<FaPrint />}
                onClick={runSelectedReprint}
                disabled={!selectedMissingRows.length || reprintBusy}
                sx={{ textTransform: "none", fontWeight: 900 }}>
                {reprintBusy ? "Reprinting…" : "Reprint Selected"}
              </Button>
              <Button size="small" variant="outlined" color="error"
                onClick={() => {
                  const text = missingRows
                    .map((row) => [row.id, row.account, row.order_id, row.order_item_id, row.source_state, row.dispatch_by, row.reason].filter(Boolean).join("\t"))
                    .join("\n");
                  try { navigator.clipboard.writeText(text); } catch { /* ignore */ }
                }}
                sx={{ textTransform: "none" }}>
                Copy List
              </Button>
              {reprintResult?.relpath ? (
                <>
                  <Button size="small" variant="contained" startIcon={<FaPrint />}
                    onClick={() => openArchiveFile(reprintResult.relpath, reprintResult.name, false)}
                    sx={{ textTransform: "none", fontWeight: 900 }}>
                    Open & Print
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<FaDownload />}
                    onClick={() => openArchiveFile(reprintResult.relpath, reprintResult.name, true)}
                    sx={{ textTransform: "none", fontWeight: 900 }}>
                    Download
                  </Button>
                </>
              ) : null}
            </Stack>
            {reprintResult ? (
              <Box sx={{
                p: 1,
                border: "1px solid",
                borderColor: reprintResult.ok ? "success.main" : "error.main",
                borderRadius: 1,
                bgcolor: reprintResult.ok ? "#e8f5e9" : "#ffebee",
              }}>
                <Typography variant="body2" sx={{
                  fontWeight: 800,
                  color: reprintResult.ok ? "success.main" : "error.main",
                }}>
                  {reprintResult.ok
                    ? `Reprint ready: ${reprintResult.printed || 0} label(s) merged${reprintResult.pages ? ` · ${reprintResult.pages} page(s)` : ""}`
                    : `Reprint failed: ${reprintResult.error || "no labels printed"}`}
                </Typography>
                {Array.isArray(reprintResult.failed) && reprintResult.failed.length ? (
                  <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "error.main", fontWeight: 700 }}>
                    Failed: {reprintResult.failed.map((row) => `${row.tracking}: ${row.reason || "failed"}`).join(" · ")}
                  </Typography>
                ) : null}
              </Box>
            ) : null}
            <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
              <Box sx={{
                display: "grid",
                gridTemplateColumns: "42px 1.1fr 0.8fr 1.1fr 1.1fr 1.8fr",
                gap: 1,
                px: 1,
                py: 0.75,
                bgcolor: "action.hover",
                fontWeight: 800,
              }}>
                <Checkbox
                  size="small"
                  checked={!!missingRows.length && selectedMissingRows.length === missingRows.length}
                  indeterminate={selectedMissingRows.length > 0 && selectedMissingRows.length < missingRows.length}
                  onChange={(e) => setAllMissingSelected(e.target.checked)}
                  sx={{ p: 0 }}
                />
                <Typography variant="caption">Tracking ID</Typography>
                <Typography variant="caption">Account</Typography>
                <Typography variant="caption">Order ID</Typography>
                <Typography variant="caption">Order Item ID</Typography>
                <Typography variant="caption">Reason</Typography>
              </Box>
              <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
                {missingRows.map((row) => (
                  <Box key={row.id} sx={{
                    display: "grid",
                    gridTemplateColumns: "42px 1.1fr 0.8fr 1.1fr 1.1fr 1.8fr",
                    gap: 1,
                    px: 1,
                    py: 0.75,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    alignItems: "center",
                  }}>
                    <Checkbox
                      size="small"
                      checked={selectedMissingIds.has(String(row.id || "").trim())}
                      onChange={(e) => toggleMissingId(row.id, e.target.checked)}
                      sx={{ p: 0 }}
                    />
                    <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700 }}>
                      {row.id}
                    </Typography>
                    <Typography variant="body2">{row.account || "-"}</Typography>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{row.order_id || "-"}</Typography>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{row.order_item_id || "-"}</Typography>
                    <Typography variant="body2">
                      {row.reason || row.source_state || "-"}
                    </Typography>
                  </Box>
                ))}
                {!missingRows.length ? (
                  <Typography variant="body2" sx={{ p: 2, opacity: 0.7 }}>
                    No missing scan tracking IDs.
                  </Typography>
                ) : null}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      <ConsoleDownloadsDialog
        open={dlOpen}
        onClose={() => setDlOpen(false)}
        source="flipkart"
        title="📥 Flipkart browser downloads"
      />
    </Box>
  );
}
